import React, { useEffect, useState, useRef } from "react";
import SwipeItem from "./SwipeItem";
import api, {
  ApiResponse,
  updateOrderStatus,
  cancelOrderItem,
  cancelOrder,
  updateOrderScheduledAt,
  verifyEmployeePasscode,
  processOrderRefunds,
} from "../../services/api";
import {
  ArrowUpDown,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  History,
  LogOut,
  Menu,
  Printer,
  RefreshCcw,
  Search,
  Settings,
  UtensilsCrossed,
} from "lucide-react";
import call from "../../assests/phone-call.png";
import box from "../../assests/box.svg";
import online from "../../assests/online.svg";
import spoon from "../../assests/spoons.svg";
import check from "../../assests/check.svg";
import curbside from "../../assests/curbside.svg";
import counter from "../../assests/counter.png";
import { useSettings } from "./SettingsContext";
import Buzz, { BuzzHandle } from "./Buzz";
import Cookies from "js-cookie";
import { Order, KitchenOrder, OrderItem } from "../../utils";
import { useKitchenAuth } from "../../context/KitchenAuthContext";
import { printKitchenUniversal } from "../../pages/pos/universalPrint";
import {
  getKitchenPrinterName,
} from "../../pages/pos/bluetoothPrinter";
import { ReceiptOrder } from "../../pages/pos/receiptPrint";
import KitchenPrinterModal from "../../components/KitchenPrinterModal";
import Setting from "./Setting";

/** metadata stored when an order is marked "done" */
interface DoneMeta {
  prepTimeSeconds: number;
  readyAt: number; // timestamp (ms) when pickup window ends
  markedAt: number; // timestamp when marked done
}

interface ParsedCustomization {
  added: string[];
  removed: string[];
  left: string[];
  whole: string[];
  right: string[];
}

const DEFAULT_READY_SECONDS = 10 * 60; // default 10 minutes pickup window
const DEFAULT_PREP_SECONDS = 10 * 60; // default 10 minutes prep window
const PICKED_UP_RECENT_VISIBLE_MS = 60 * 1000; // keep pickedup orders visible in recent for 1 minute
const PRINTED_STORAGE_PREFIX = "kitchenPrintedOrders";
const ORDERS_BATCH_SIZE = 13;

type KitchenViewMode = "recent" | "scheduled" | "dinein" | "history";
const ORDERS_ROW_COLUMNS_CLASS =
  "grid-cols-[minmax(150px,1.1fr)_minmax(70px,0.45fr)_minmax(140px,0.9fr)_minmax(120px,0.8fr)_30px] sm:grid-cols-[minmax(160px,1.15fr)_minmax(84px,0.45fr)_minmax(160px,0.9fr)_minmax(130px,0.85fr)_32px] md:grid-cols-[minmax(170px,1.2fr)_minmax(96px,0.5fr)_minmax(180px,0.95fr)_minmax(140px,0.9fr)_34px] lg:grid-cols-[minmax(190px,1.3fr)_minmax(110px,0.55fr)_minmax(210px,1fr)_minmax(170px,0.9fr)_40px] xl:grid-cols-[minmax(220px,1.35fr)_minmax(130px,0.55fr)_minmax(250px,1fr)_minmax(220px,0.95fr)_44px]";

const getOrderNumberLabel = (order: { orderNumber?: string; _id: string }) =>
  order.orderNumber != null
    ? `#${order.orderNumber}`
    : `#${order._id.slice(-6).toUpperCase()}`;
const formatChoiceLabel = (choice?: string) => {
  if (!choice) return "CounterSide";
  const normalized = choice.toLowerCase();
  if (normalized === "dining in") return "Dining In";
  if (normalized === "takeaway") return "Takeaway";
  if (normalized === "online") return "Online";
  if (normalized === "curbside") return "CurbSide";
  return choice;
};

const getLatestNormalizedDeliveryStatus = (
  order: Pick<KitchenOrder, "deliveryStatus" | "deliveryStatusHistory">,
) => {
  const latestHistoryStatus = Array.isArray(order.deliveryStatusHistory)
    ? [...order.deliveryStatusHistory]
        .reverse()
        .find(
          (entry) => typeof entry?.status === "string" && entry.status.trim(),
        )?.status
    : "";
  return String(latestHistoryStatus || order.deliveryStatus || "")
    .trim()
    .toLowerCase();
};

const isDeliveryClosedOrder = (
  order: Pick<KitchenOrder, "deliveryStatus" | "deliveryStatusHistory">,
) => {
  const status = getLatestNormalizedDeliveryStatus(order);
  return status === "delivered" || status === "cancelled" || status === "canceled";
};

const hasDropoffAddressInMetadata = (
  order: Pick<KitchenOrder, "metadata">,
) => {
  const metadata = order.metadata as Record<string, unknown> | undefined;
  if (!metadata || typeof metadata !== "object") return false;

  const directAddressCandidates = [
    metadata.dropoff_address,
    metadata.dropoffAddress,
    metadata.delivery_address,
    metadata.deliveryAddress,
  ];
  const hasDirectAddress = directAddressCandidates.some(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  if (hasDirectAddress) return true;

  const dropoff = metadata.dropoff as Record<string, unknown> | undefined;
  if (dropoff && typeof dropoff === "object") {
    const nestedCandidates = [dropoff.address, dropoff.full_address];
    return nestedCandidates.some(
      (value) => typeof value === "string" && value.trim().length > 0,
    );
  }

  return false;
};

const calculateOriginalOrderTotal = (order: KitchenOrder) => {
  const subtotal = (order.items || []).reduce((sum, item) => {
    const qty = typeof item.quantity === "number" ? item.quantity : 1;
    let itemTotal = (item.price ?? 0) * qty;

    if (
      typeof item.discountAmount === "number" &&
      !Number.isNaN(item.discountAmount)
    ) {
      itemTotal -= item.discountAmount;
    }

    if (Array.isArray(item.customOptions)) {
      for (const option of item.customOptions) {
        if (!option) continue;
        const optionStatus = String((option as any).status || "").toLowerCase();
        if (optionStatus === "cancelled") continue;
        const optionQty = typeof option.qty === "number" ? option.qty : 1;
        itemTotal += (option.price ?? 0) * optionQty;
      }
    }

    if (itemTotal < 0) itemTotal = 0;
    return sum + itemTotal;
  }, 0);

  const orderDiscountRaw = (order as any).discountAmount;
  const orderDiscount =
    typeof orderDiscountRaw === "number" && !Number.isNaN(orderDiscountRaw)
      ? orderDiscountRaw
      : 0;

  const total = subtotal - orderDiscount;
  return Math.max(0, Math.round(total * 100) / 100);
};

const formatScheduledDateTime = (
  scheduledAt?: string | Date,
  scheduledTimezone?: string,
) => {
  if (!scheduledAt) return "";
  const scheduledDate = new Date(scheduledAt);
  if (Number.isNaN(scheduledDate.getTime())) return "";

  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: scheduledTimezone || undefined,
    }).format(scheduledDate);
    const timezoneLabel = getTimezoneAbbreviation(
      scheduledDate,
      scheduledTimezone,
    );
    return timezoneLabel ? `${formatted} ${timezoneLabel}` : formatted;
  } catch {
    return scheduledDate.toLocaleString();
  }
};

const DEFAULT_ORDER_TIMEZONE =
  process.env.REACT_APP_ORDER_FALLBACK_TIMEZONE?.trim() || "America/Vancouver";

const isValidTimezone = (value?: string) => {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const getTimezoneAbbreviation = (date: Date, timezone?: string) => {
  if (!timezone) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(date);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};

const resolveOrderTimezone = (order: KitchenOrder) => {
  const metadata = order.metadata as Record<string, unknown> | undefined;
  const timezoneCandidates = [
    order.scheduledTimezone,
    typeof metadata?.scheduledTimezone === "string"
      ? metadata.scheduledTimezone
      : undefined,
    typeof metadata?.timeZone === "string" ? metadata.timeZone : undefined,
    typeof metadata?.timezone === "string" ? metadata.timezone : undefined,
    typeof metadata?.pickup_timezone === "string"
      ? metadata.pickup_timezone
      : undefined,
    typeof metadata?.dropoff_timezone === "string"
      ? metadata.dropoff_timezone
      : undefined,
    DEFAULT_ORDER_TIMEZONE,
  ];

  return timezoneCandidates.find((tz) => isValidTimezone(tz));
};

const formatOrderTime = (
  dateTime?: string | Date,
  scheduledTimezone?: string,
) => {
  if (!dateTime) return "";
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return "";

  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: scheduledTimezone || undefined,
    }).format(date);
    const timezoneLabel = getTimezoneAbbreviation(date, scheduledTimezone);
    return timezoneLabel ? `${formatted} ${timezoneLabel}` : formatted;
  } catch {
    return date.toLocaleTimeString();
  }
};

const normalizeStatusTimestamp = (value: unknown): string | Date | undefined => {
  if (value instanceof Date || typeof value === "string") return value;
  if (typeof value === "number") return new Date(value);
  if (value && typeof value === "object" && "$date" in value) {
    const nestedValue = (value as { $date?: unknown }).$date;
    if (nestedValue instanceof Date || typeof nestedValue === "string") {
      return nestedValue;
    }
    if (typeof nestedValue === "number") return new Date(nestedValue);
  }
  return undefined;
};

const getLatestStatusTimestamp = (
  statusHistory: { status: string; updatedAt: unknown }[] = [],
  statuses: string[],
): number | undefined => {
  const statusSet = new Set(statuses.map((status) => status.toLowerCase()));
  let latestTimestamp: number | undefined;

  statusHistory.forEach((entry) => {
    const statusKey =
      typeof entry?.status === "string"
        ? entry.status.trim().toLowerCase()
        : "";
    if (!statusSet.has(statusKey)) return;

    const normalizedUpdatedAt = normalizeStatusTimestamp(entry.updatedAt);
    if (!normalizedUpdatedAt) return;
    const timestamp = new Date(normalizedUpdatedAt).getTime();
    if (Number.isNaN(timestamp)) return;

    if (latestTimestamp === undefined || timestamp > latestTimestamp) {
      latestTimestamp = timestamp;
    }
  });

  return latestTimestamp;
};

const getOptionLabelWithQty = (label: string, qty: number) =>
  qty > 1 ? `${label} ×${qty}` : label;

const parseItemCustomizations = (
  options: { label: string; price: number; qty: number }[] = [],
): ParsedCustomization =>
  options.reduce<ParsedCustomization>(
    (acc, opt) => {
      const rawLabel = opt.label?.trim();
      if (!rawLabel) return acc;

      if (opt.qty === -1) {
        acc.removed.push(rawLabel);
        return acc;
      }

      const sideMatch = rawLabel.match(/^(.*?)\s+(left|right|whole)$/i);
      if (!sideMatch) {
        acc.added.push(getOptionLabelWithQty(rawLabel, opt.qty));
        return acc;
      }

      const baseLabel = sideMatch[1].trim();
      const side = sideMatch[2].toLowerCase();
      if (!baseLabel) return acc;

      const formattedLabel = getOptionLabelWithQty(baseLabel, opt.qty);
      if (side === "left") acc.left.push(formattedLabel);
      if (side === "whole") acc.whole.push(formattedLabel);
      if (side === "right") acc.right.push(formattedLabel);
      return acc;
    },
    { added: [], removed: [], left: [], whole: [], right: [] },
  );

interface ItemCustomizationsProps {
  item: OrderItem;
  textClassName?: string;
  showInstructions?: boolean;
}

const ItemCustomizations: React.FC<ItemCustomizationsProps> = ({
  item,
  textClassName = "",
  showInstructions = true,
}) => {
  const parsed = parseItemCustomizations(item.customOptions ?? []);
  const sideBlocks = [
    {
      key: "whole",
      label: "Whole",
      items: parsed.whole,
      colStart: "col-start-1",
    },
    { key: "left", label: "Left", items: parsed.left, colStart: "col-start-2" },
    {
      key: "right",
      label: "Right",
      items: parsed.right,
      colStart: "col-start-3",
    },
  ].filter((block) => block.items.length > 0);

  if (
    parsed.added.length === 0 &&
    parsed.removed.length === 0 &&
    sideBlocks.length === 0 &&
    !(showInstructions && item.requestedCustomization)
  ) {
    return null;
  }

  return (
    <div className={`mt-0.5 space-y-0.5 text-xs ${textClassName}`.trim()}>
      {parsed.added.length > 0 && (
        <p className="m-0">
          <span className="font-semibold">Added- </span>
          {parsed.added.join(", ")}
        </p>
      )}

      {parsed.removed.length > 0 && (
        <p className="m-0">
          <span className="fon t-semibold">Removed- </span>
          {parsed.removed.join(", ")}
        </p>
      )}
      {showInstructions && item.requestedCustomization && (
        <p className="m-0">
          <span className="font-semibold">Instructions: </span>
          {item.requestedCustomization}
        </p>
      )}

      {sideBlocks.length > 0 && (
        <div className="space-y-0.5">
          {sideBlocks.map((block) => (
            <div key={block.key} className="grid grid-cols-3 gap-x-2">
              <div className={`${block.colStart} text-left break-words`}>
                <p className="m-0">
                  <span className="font-semibold">{`${block.label}-`}</span>
                  {block.items[0]}
                </p>
                {block.items.slice(1).map((sideItem, index) => (
                  <p className="m-0" key={`${block.key}-${index}`}>
                    {sideItem}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
interface ScheduledCardProps {
  order: KitchenOrder;
  deletedItems: Record<string, boolean>;
  getItemKey: (orderId: string, itemId: string, index: number) => string;
  formatTime: (time: number) => string;
  onDeleteItem: (orderId: string, itemId: string, index: number) => void;
  onOpenDelayModal: (order: KitchenOrder) => void;
  onCancelOrder: (order: KitchenOrder) => void;
  isCancellingOrder: boolean;
}

interface PendingFullCancelOrder {
  orderId: string;
  orderNumber?: string;
}

const ScheduledCard: React.FC<ScheduledCardProps> = ({
  order,
  deletedItems,
  getItemKey,
  formatTime,
  onDeleteItem,
  onOpenDelayModal,
  onCancelOrder,
  isCancellingOrder,
}) => {
  const timeUntil = Math.max(
    0,
    Math.ceil((new Date(order.scheduledAt ?? 0).getTime() - Date.now()) / 1000),
  );
  const orderTimezone = resolveOrderTimezone(order);

  return (
    <div
      key={order._id}
      className="w-80 mt-3 border border-gray-300 rounded-xl overflow-hidden font-sans shadow-sm bg-white flex flex-col"
    >
      <div className="p-2 text-white text-sm bg-[#6B7280]">
        <div className="flex justify-between">
          <span className="text-right font-bold font-outfit">
            {getOrderNumberLabel(order)}
          </span>
          <span className="font-outfit">
            <span className="font-bold">Order By:</span>{" "}
            <span>{order.phoneNumber}</span>
          </span>
        </div>
        <div className="flex justify-between font-semibold items-center mt-0.5">
          {order.choice && (
            <div className="flex items-center gap-1">
              <span className="text-s font-outfit">
                {formatChoiceLabel(order.choice)}
              </span>
              {order.choice.toLowerCase() === "dining in" && (
                <img
                  src={spoon}
                  alt="Dining In"
                  className="w-[16px] h-[16px]"
                />
              )}
              {order.choice.toLowerCase() === "takeaway" && (
                <img src={box} alt="Takeaway" className="w-[16px] h-[16px]" />
              )}
              {order.choice.toLowerCase() === "online" && (
                <img src={online} alt="online" className="w-[16px] h-[16px]" />
              )}
              {order.choice.toLowerCase() === "curbside" && (
                <img
                  src={curbside}
                  alt="Curbside"
                  className="w-[16px] h-[16px]"
                />
              )}
            </div>
          )}
          <span className="font-outfit text-white text-xs font-semibold">
            {timeUntil > 0 ? `⏳ ${formatTime(timeUntil)}` : "⏰ Due now"}
          </span>
        </div>
      </div>

      <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-600 font-outfit flex items-center gap-1 border-b border-gray-200">
        <span>
          Scheduled for:{" "}
          <button
            type="button"
            onClick={() => onOpenDelayModal(order)}
            className="font-semibold text-gray-800 underline decoration-dotted underline-offset-2 hover:text-gray-900"
          >
            {formatScheduledDateTime(order.scheduledAt, orderTimezone)}
          </button>
        </span>
      </div>

      <div className="overflow-y-auto flex-1 max-h-72 no-scrollbar">
        {order.items.map((item: OrderItem, idx: number) => {
          const key = getItemKey(order._id, item.itemId, idx);
          return (
            <SwipeItem
              key={key}
              onDone={() => undefined}
              onDelete={() => {
                if (deletedItems[key] || item.itemstatus === "cancelled") {
                  return;
                }
                onDeleteItem(order._id, item.itemId, idx);
              }}
              isPreparing={false}
            >
              <div
                className={`p-3 border-b border-gray-200 text-sm ${
                  deletedItems[key] || item.itemstatus === "cancelled"
                    ? "line-through text-red-600"
                    : ""
                }`}
              >
                <div
                  className={`font-outfit ${
                    deletedItems[key] || item.itemstatus === "cancelled"
                      ? "line-through text-red-500"
                      : "text-black"
                  }`}
                >
                  <b>{item.quantity}x</b> {item.kitchenName || item.name}
                  <ItemCustomizations item={item} />
                </div>
              </div>
            </SwipeItem>
          );
        })}
      </div>

      <div className="px-3 py-2 border-t border-gray-100">
        <button
          type="button"
          onClick={() => onCancelOrder(order)}
          className="w-full py-2 border border-red-600 text-red-600 rounded-lg bg-white text-sm font-medium font-outfit disabled:opacity-50"
          disabled={isCancellingOrder}
        >
          Cancel Order
        </button>
      </div>

      <div className="px-3 py-2 text-xs text-black font-outfit bg-white w-full border-t border-gray-100">
        Ordered at: {formatOrderTime(order.createdAt, orderTimezone)}
      </div>
    </div>
  );
};

const KitchenOrders: React.FC = () => {
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [deletedItems, setDeletedItems] = useState<Record<string, boolean>>({});
  const [visibleItems, setVisibleItems] = useState<Record<string, boolean>>({});
  const [doneMeta, setDoneMeta] = useState<Record<string, DoneMeta>>({});
  const [activeView, setActiveView] = useState<KitchenViewMode>("recent");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [unreadRecentOrderIds, setUnreadRecentOrderIds] = useState<Set<string>>(
    new Set(),
  );
  const [unreadScheduledOrderIds, setUnreadScheduledOrderIds] = useState<
    Set<string>
  >(new Set());
  const [unreadDineInOrderIds, setUnreadDineInOrderIds] = useState<
    Set<string>
  >(new Set());
  const [expandedOrderRows, setExpandedOrderRows] = useState<
    Record<string, boolean>
  >({});
  const [selectedOrder, setSelectedOrder] = useState<KitchenOrder | null>(null);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [visibleOrderCount, setVisibleOrderCount] = useState(ORDERS_BATCH_SIZE);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [scheduleModalOrder, setScheduleModalOrder] =
    useState<KitchenOrder | null>(null);
  const [isCancellingItem, setIsCancellingItem] = useState(false);
  const [pendingFullCancelOrder, setPendingFullCancelOrder] =
    useState<PendingFullCancelOrder | null>(null);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);

  // Passcode modal state
  type PasscodePendingAction =
    | {
        type: "deleteItem";
        orderId: string;
        itemId: string;
        index: number;
        itemName: string;
        orderNumber?: string;
      }
    | { type: "cancelOrder"; orderId: string; orderNumber?: string };
  const [passcodePendingAction, setPasscodePendingAction] =
    useState<PasscodePendingAction | null>(null);
  const [passcodeInput, setPasscodeInput] = useState("");
  const [passcodeError, setPasscodeError] = useState("");
  const [isVerifyingPasscode, setIsVerifyingPasscode] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState(0);
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);
  const [showKitchenPrinterModal, setShowKitchenPrinterModal] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const { settings, sortOrder, setSortOrder } = useSettings();
  const buzzRef = useRef<BuzzHandle>(null);
  const seenOrderIdsRef = useRef<Set<string>>(new Set());
  const hasInitializedOrdersRef = useRef(false);
  const passcodeInputRef = useRef<HTMLInputElement>(null);
  const { user } = useKitchenAuth();
  const printedOrderIdsRef = useRef<Set<string>>(new Set());
  const pendingPrintMapRef = useRef<Map<string, KitchenOrder>>(new Map());
  const isPrintingRef = useRef(false);
  const hasLoadedPrintedRef = useRef(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const ordersScrollRef = useRef<HTMLDivElement>(null);
  const bodyOverflowRef = useRef<string>("");

  // Convert KitchenOrder → ReceiptOrder shape expected by the BLE ticket builder.
  // Uses kitchenName (if set) as the display name on the ticket.
  const toReceiptOrder = (order: KitchenOrder): ReceiptOrder => ({
    _id: order._id,
    orderNumber: order.orderNumber,
    createdAt: order.createdAt,
    totalAmount: order.totalAmount ?? 0,
    choice: order.choice,
    tableToken: order.tableToken,
    employeeName: order.employeeName,
    metadata: order.metadata,
    phoneNumber:
      order.phoneNumber != null ? String(order.phoneNumber).trim() : undefined,
    orderBy: order.orderBy,
    isScheduled: order.isScheduled,
    scheduledAt: order.scheduledAt,
    scheduledTimezone: order.scheduledTimezone,
    items: order.items.map((item) => ({
      itemId: item.itemId,
      name: item.kitchenName || item.name,
      quantity: item.quantity,
      price: item.price,
      itemstatus: item.itemstatus,
      customOptions: item.customOptions,
    })),
  });

  // Enable audio playback on first user interaction (unlocks autoplay restrictions)
  useEffect(() => {
    const enableAudio = () => {
      setIsAudioEnabled(true);
      buzzRef.current?.play();
      document.removeEventListener("click", enableAudio);
      document.removeEventListener("touchstart", enableAudio);
    };

    document.addEventListener("click", enableAudio, { once: true });
    document.addEventListener("touchstart", enableAudio, { once: true });

    return () => {
      document.removeEventListener("click", enableAudio);
      document.removeEventListener("touchstart", enableAudio);
    };
  }, []);

  useEffect(() => {
    console.log("user", user);
  }, [user]);

  useEffect(() => {
    if (!user?.restaurantId) return;
    try {
      const key = `${PRINTED_STORAGE_PREFIX}:${user.restaurantId}`;
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        printedOrderIdsRef.current = new Set(
          parsed.filter((id) => typeof id === "string"),
        );
      } else {
        printedOrderIdsRef.current = new Set();
      }
    } catch {
      printedOrderIdsRef.current = new Set();
    } finally {
      hasLoadedPrintedRef.current = true;
    }
  }, [user?.restaurantId]);

  const persistPrinted = () => {
    if (!user?.restaurantId) return;
    try {
      const key = `${PRINTED_STORAGE_PREFIX}:${user.restaurantId}`;
      const ids = Array.from(printedOrderIdsRef.current);
      localStorage.setItem(key, JSON.stringify(ids));
    } catch (err) {
      console.warn("Failed to persist printed orders", err);
    }
  };

  const markPrinted = (orderId: string) => {
    printedOrderIdsRef.current.add(orderId);
    persistPrinted();
  };

  const processPrintQueue = async () => {
    if (isPrintingRef.current) return;
    if (!pendingPrintMapRef.current.size) return;
    isPrintingRef.current = true;
    try {
      while (pendingPrintMapRef.current.size) {
        const nextEntry = pendingPrintMapRef.current.entries().next();
        if (nextEntry.done) break;
        const [orderId, order] = nextEntry.value;
        pendingPrintMapRef.current.delete(orderId);
        try {
          await printKitchenUniversal(toReceiptOrder(order));
          markPrinted(orderId);
        } catch (err) {
          if (err instanceof Error && err.message === "NO_KITCHEN_PRINTER") {
            // No printer paired — silently skip, user can pair via settings
            console.warn("Kitchen print skipped: no printer paired", orderId);
          } else {
            console.error("Kitchen print failed", orderId, err);
          }
        }
      }
    } finally {
      isPrintingRef.current = false;
    }
  };

  const enqueueKitchenPrint = (ordersToPrint: KitchenOrder[]) => {
    if (!ordersToPrint.length) return;
    const sorted = [...ordersToPrint].sort((a, b) => {
      const aNum = Number(a.orderNumber);
      const bNum = Number(b.orderNumber);
      if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) {
        return aNum - bNum;
      }
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return aTime - bTime;
    });
    sorted.forEach((order) => {
      if (!pendingPrintMapRef.current.has(order._id)) {
        pendingPrintMapRef.current.set(order._id, order);
      }
    });
    void processPrintQueue();
  };

  const triggerNotification = () => {
    buzzRef.current?.play();
  };

  const getOrderScheduleBaseTime = (order: KitchenOrder) =>
    new Date(order.scheduledAt ?? order.createdAt).getTime();
  const getOrderSortTime = (order: KitchenOrder) => {
    const timestamp = new Date(order.scheduledAt ?? order.createdAt).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const openDelayScheduleModal = (order: KitchenOrder) => {
    setScheduleModalOrder(order);
    setDelayMinutes(0);
  };

  const closeDelayScheduleModal = () => {
    if (isUpdatingSchedule) return;
    setScheduleModalOrder(null);
    setDelayMinutes(0);
  };

  const applyScheduledDelay = async () => {
    if (!scheduleModalOrder || delayMinutes <= 0 || isUpdatingSchedule) return;

    const baseTime = getOrderScheduleBaseTime(scheduleModalOrder);
    const nextScheduledIso = new Date(
      baseTime + delayMinutes * 60 * 1000,
    ).toISOString();

    try {
      setIsUpdatingSchedule(true);
      const response = await updateOrderScheduledAt(
        scheduleModalOrder._id,
        nextScheduledIso,
      );

      if (response.data?.success) {
        const updatedScheduledAt =
          response.data.data?.scheduledAt ?? nextScheduledIso;

        setOrders((prev) =>
          prev.map((order) =>
            order._id === scheduleModalOrder._id
              ? {
                  ...order,
                  ...response.data.data,
                  orderNumber:
                    response.data.data?.orderNumber ?? order.orderNumber,
                  scheduledAt: updatedScheduledAt,
                  isScheduled: true,
                  orderStatus: "scheduled",
                }
              : order,
          ),
        );

        setScheduleModalOrder(null);
        setDelayMinutes(0);
      }
    } catch (error) {
      console.error("Failed to delay scheduled order", error);
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (
        isSortMenuOpen &&
        sortMenuRef.current &&
        !sortMenuRef.current.contains(event.target as Node)
      ) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isSortMenuOpen]);

  // Fetch orders (wait until kitchen user/restaurant is available)
  useEffect(() => {
    if (!user?.restaurantId) return;
    fetchOrders(1);
  }, [user?.restaurantId, activeView]);

  useEffect(() => {
    if (!selectedOrder && !showSettingsPanel && !passcodePendingAction) return;
    bodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyOverflowRef.current;
    };
  }, [selectedOrder, showSettingsPanel, passcodePendingAction]);

  useEffect(() => {
    if (!selectedOrder) return;
    const updatedOrder = orders.find((order) => order._id === selectedOrder._id);
    if (updatedOrder) {
      setSelectedOrder(updatedOrder);
    }
  }, [orders, selectedOrder?._id]);

  // Polling disabled - orders now received via socket in real-time
  // useEffect(() => {
  //   if (!user?.restaurantId) return;
  //   const interval = setInterval(() => {
  //     fetchOrders(1);
  //   }, 10000);
  //
  //   return () => clearInterval(interval);
  // }, [user?.restaurantId, activeView]);

  // Socket listener for new orders
  useEffect(() => {
    if (!user?.restaurantId) return;

    const wsUrl = (() => {
      const baseUrl = process.env.REACT_APP_BACKEND_WS_URL ||
        (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
        window.location.hostname + ':' + (window.location.port || (window.location.protocol === 'https:' ? 443 : 80));
      const secret = process.env.REACT_APP_PRINT_SERVICE_SECRET;
      return secret ? `${baseUrl}?secret=${encodeURIComponent(secret)}` : baseUrl;
    })();

    const ws = new WebSocket(wsUrl);
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const registerRestaurant = () => {
      ws.send(JSON.stringify({ type: "register", restaurantId: user.restaurantId }));
    };

    ws.onopen = () => {
      registerRestaurant();
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string);
        if (msg.type !== "new-order" || !msg.order) return;

        const order = msg.order;
        if (!order?._id) return;

        // Check if order already exists
        if (seenOrderIdsRef.current.has(order._id)) return;

        console.log(`[KitchenSocket] New order received: #${order.orderNumber} status=${order.orderStatus}`);

        // Transform order like fetchOrders does
        const preparingStartedAt = getPreparingTime(order.statusHistory || []);
        const newOrder: KitchenOrder = {
          ...order,
          cardBackground:
            order.orderStatus === "received"
              ? "#7CBF80"
              : order.orderStatus === "preparing"
                ? "#F2B55D"
                : order.orderStatus === "scheduled"
                  ? "#6B7280"
                  : "",
          preparingStartedAt,
          timeRemaining: 0,
        };

        // Determine which unread set(s) to add to
        const isRecentReceivedOrder = newOrder.orderStatus === "received" && !isDeliveryClosedOrder(newOrder);
        const isScheduledOrder = newOrder.orderStatus === "scheduled" && new Date(newOrder.scheduledAt ?? 0).getTime() > Date.now();
        const isDineInOrder = newOrder.choice?.toLowerCase() === "dining in" && newOrder.orderStatus !== "cancelled";

        let shouldBuzz = false;

        if (isRecentReceivedOrder) {
          setUnreadRecentOrderIds((prev) => {
            const next = new Set(prev);
            next.add(newOrder._id);
            return next;
          });
          shouldBuzz = true;
          console.log(`[KitchenSocket] Added to unread recent: #${order.orderNumber}`);
        }

        if (isScheduledOrder) {
          setUnreadScheduledOrderIds((prev) => {
            const next = new Set(prev);
            next.add(newOrder._id);
            return next;
          });
          shouldBuzz = true;
          console.log(`[KitchenSocket] Added to unread scheduled: #${order.orderNumber}`);
        }

        if (isDineInOrder) {
          setUnreadDineInOrderIds((prev) => {
            const next = new Set(prev);
            next.add(newOrder._id);
            return next;
          });
          shouldBuzz = true;
          console.log(`[KitchenSocket] Added to unread dine-in: #${order.orderNumber}`);
        }

        // Add to orders state
        seenOrderIdsRef.current.add(newOrder._id);
        setOrders((prev) => [newOrder, ...prev]);

        // Trigger buzz if order was added to any unread set
        if (shouldBuzz) {
          console.log(`[KitchenSocket] Starting buzzer for #${order.orderNumber}`, { buzzRef: buzzRef.current });
          buzzRef.current?.startRinging();
        } else {
          console.log(`[KitchenSocket] Not buzzing for #${order.orderNumber} (shouldBuzz=${shouldBuzz})`);
        }

        // Enqueue for printing
        if (hasLoadedPrintedRef.current) {
          enqueueKitchenPrint([newOrder]);
        }
      } catch (err) {
        console.error("[KitchenSocket] Message parse error:", err);
      }
    };

    ws.onerror = () => {
      console.warn("[KitchenSocket] Connection error");
    };

    ws.onclose = () => {
      if (!reconnectTimeout) {
        reconnectTimeout = setTimeout(() => {
          reconnectTimeout = null;
        }, 5000);
      }
    };

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      ws.close();
    };
  }, [user?.restaurantId]);

  const getPreparingTime = (
    statusHistory: { status: string; updatedAt: any }[],
  ): number | undefined => {
    return getLatestStatusTimestamp(statusHistory, ["preparing"]);
  };

  const getApiPrepSeconds = (order: KitchenOrder) => {
    const raw = order.orderPrepTime;
    if (typeof raw !== "number" || raw <= 0) return undefined;
    if (raw > 300) return Math.round(raw);
    return Math.round(raw * 60);
  };

  const getPrepTargetSeconds = (order: KitchenOrder) =>
    getApiPrepSeconds(order) ?? DEFAULT_PREP_SECONDS;

  const getPrepTimeSeconds = (
    order: KitchenOrder,
    completedAt?: number,
  ): number | undefined => {
    const preparingAt =
      getPreparingTime(order.statusHistory || []) ?? order.preparingStartedAt;
    if (preparingAt != null && completedAt != null) {
      return Math.max(0, Math.round((completedAt - preparingAt) / 1000));
    }
    const apiSeconds = getApiPrepSeconds(order);
    if (apiSeconds) return apiSeconds;
    return undefined;
  };

  const getReadyCompletedTime = (
    statusHistory: { status: string; updatedAt: any }[],
  ): number | undefined => {
    return getLatestStatusTimestamp(statusHistory, [
      "ready",
      "completed",
      "complete",
      "done",
    ]);
  };

  const getPickedUpTime = (
    statusHistory: { status: string; updatedAt: any }[],
  ): number | undefined => {
    return getLatestStatusTimestamp(statusHistory, ["pickedup"]);
  };

  const shouldShowPickedUpInRecent = (order: KitchenOrder) => {
    if (order.orderStatus !== "pickedup") return true;
    const pickedUpAt = getPickedUpTime(order.statusHistory || []);
    if (pickedUpAt == null) return false;
    return Date.now() - pickedUpAt <= PICKED_UP_RECENT_VISIBLE_MS;
  };

  const getPickupDeadline = (order: KitchenOrder): number | undefined => {
    const completedAt =
      getReadyCompletedTime(order.statusHistory || []) ??
      doneMeta[order._id]?.markedAt;
    if (completedAt != null) {
      return completedAt + DEFAULT_READY_SECONDS * 1000;
    }
    return doneMeta[order._id]?.readyAt;
  };
useEffect(() => {
  const totalUnread =
    unreadRecentOrderIds.size +
    unreadScheduledOrderIds.size +
    unreadDineInOrderIds.size;

  if (totalUnread === 0) {
    buzzRef.current?.stopRinging();
  }
}, [unreadRecentOrderIds, unreadScheduledOrderIds, unreadDineInOrderIds]);
  const fetchOrders = async (page = 1) => {
    try {
      if (!user?.restaurantId) return;
      const isHistoryView = activeView === "history";
      const limit = isHistoryView ? 200 : 40;
      const response = await api.get<ApiResponse<Order[]>>(
        `/orders?page=${page}&limit=${limit}&restaurantId=${user.restaurantId}`,
      );
      if (response.data?.success) {
        const now = Date.now();

        // Today at 00:00
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStartTs = todayStart.getTime();

        const mapped = response.data.data
          // For history view keep all statuses and dates.
          // For operational views keep existing live-order behavior.
          .filter((order) =>
            isHistoryView ? true : order.orderStatus !== "cancelled",
          )
          .filter((order) => {
            if (isHistoryView) return true;
            const orderTime = order.scheduledAt
              ? new Date(order.scheduledAt).getTime()
              : new Date(order.createdAt).getTime();
            return orderTime >= todayStartTs;
          })
          .map((order) => {
            const preparingStartedAt = getPreparingTime(
              order.statusHistory || [],
            );
            return {
              ...order,
              cardBackground:
                order.orderStatus === "received"
                  ? "#7CBF80"
                  : order.orderStatus === "preparing"
                    ? "#F2B55D"
                    : order.orderStatus === "scheduled"
                      ? "#6B7280"
                      : "",
              preparingStartedAt,
            };
          }) as KitchenOrder[];

        const isRecentReceivedOrder = (order: KitchenOrder) =>
          order.orderStatus === "received" && !isDeliveryClosedOrder(order);
        const isScheduledOrder = (order: KitchenOrder) =>
          order.orderStatus === "scheduled" &&
          new Date(order.scheduledAt ?? 0).getTime() > Date.now();
        const isDineInOrder = (order: KitchenOrder) =>
          order.choice?.toLowerCase() === "dining in" &&
          order.orderStatus !== "cancelled";

        const currentRecentIds = new Set(
          mapped.filter(isRecentReceivedOrder).map((order) => order._id),
        );
        const currentScheduledIds = new Set(
          mapped.filter(isScheduledOrder).map((order) => order._id),
        );
        const currentDineInIds = new Set(
          mapped.filter(isDineInOrder).map((order) => order._id),
        );

        setUnreadRecentOrderIds((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(
            Array.from(prev).filter((orderId) => currentRecentIds.has(orderId)),
          );
          return next.size === prev.size ? prev : next;
        });
        setUnreadScheduledOrderIds((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(
            Array.from(prev).filter((orderId) => currentScheduledIds.has(orderId)),
          );
          return next.size === prev.size ? prev : next;
        });
        setUnreadDineInOrderIds((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(
            Array.from(prev).filter((orderId) => currentDineInIds.has(orderId)),
          );
          return next.size === prev.size ? prev : next;
        });

        if (!hasInitializedOrdersRef.current) {
          mapped.forEach((order) => {
            seenOrderIdsRef.current.add(order._id);
          });
          hasInitializedOrdersRef.current = true;
        } else {
          const newIncomingOrders =
            page === 1 &&
            mapped
              .filter((order) => !seenOrderIdsRef.current.has(order._id));
          const newRecentOrderIds = (newIncomingOrders || [])
            .filter(isRecentReceivedOrder)
            .map((order) => order._id);
          const newScheduledOrderIds = (newIncomingOrders || [])
            .filter(isScheduledOrder)
            .map((order) => order._id);
          const newDineInOrderIds = (newIncomingOrders || [])
            .filter(isDineInOrder)
            .map((order) => order._id);

          mapped.forEach((order) => {
            seenOrderIdsRef.current.add(order._id);
          });

    if (
  !isHistoryView &&
  (newRecentOrderIds.length > 0 ||
    newScheduledOrderIds.length > 0 ||
    newDineInOrderIds.length > 0)
) {
  buzzRef.current?.startRinging();
  setUnreadRecentOrderIds((prev) => {
              const next = new Set(prev);
              newRecentOrderIds.forEach((orderId) => next.add(orderId));
              return next;
            });
            setUnreadScheduledOrderIds((prev) => {
              const next = new Set(prev);
              newScheduledOrderIds.forEach((orderId) => next.add(orderId));
              return next;
            });
            setUnreadDineInOrderIds((prev) => {
              const next = new Set(prev);
              newDineInOrderIds.forEach((orderId) => next.add(orderId));
              return next;
            });
          }
        }

        if (hasLoadedPrintedRef.current) {
          const toPrint = mapped.filter(
            (order) => !printedOrderIdsRef.current.has(order._id),
          );
          enqueueKitchenPrint(toPrint);
        }

        setOrders((prevOrders) => {
          const prevMap = new Map(prevOrders.map((o) => [o._id, o]));

          return mapped.map((newOrder) => {
            const existing = prevMap.get(newOrder._id);

            // 🟢 Order already exists → KEEP timers
            if (existing) {
              return {
                ...existing,
                ...newOrder,
                timeRemaining: existing.timeRemaining,
                isLate: existing.isLate,
                showTimer: existing.showTimer,
                preparingStartedAt: existing.preparingStartedAt,
              };
            }

            // 🟢 NEW order → initialize timers
            return newOrder;
          });
        });

        // Initialize/refresh doneMeta for ready orders using persisted completed time.
        setDoneMeta((prev) => {
          let hasChanges = false;
          const next = { ...prev };

          mapped.forEach((order) => {
            if (order.orderStatus !== "ready") return;

            const existingMeta = prev[order._id];
            const completedAt =
              getReadyCompletedTime(order.statusHistory || []) ??
              existingMeta?.markedAt;

            if (completedAt == null) {
              if (existingMeta) return;
              const fallbackMarkedAt = now;
              next[order._id] = {
                prepTimeSeconds:
                  getPrepTimeSeconds(order, fallbackMarkedAt) ??
                  order.timeRemaining ??
                  0,
                readyAt: fallbackMarkedAt + DEFAULT_READY_SECONDS * 1000,
                markedAt: fallbackMarkedAt,
              };
              hasChanges = true;
              return;
            }

            const prepSeconds =
              getPrepTimeSeconds(order, completedAt) ??
              existingMeta?.prepTimeSeconds ??
              order.timeRemaining ??
              0;
            const nextMeta: DoneMeta = {
              prepTimeSeconds: prepSeconds,
              readyAt: completedAt + DEFAULT_READY_SECONDS * 1000,
              markedAt: completedAt,
            };

            if (
              !existingMeta ||
              existingMeta.markedAt !== nextMeta.markedAt ||
              existingMeta.readyAt !== nextMeta.readyAt
            ) {
              next[order._id] = nextMeta;
              hasChanges = true;
            }
          });

          return hasChanges ? next : prev;
        });
      }
    } catch (err) {
      console.error("Failed to fetch orders", err);
    }
  };

  useEffect(() => {
    orders.forEach((order) => {
      order.items.forEach((item, idx) => {
        if (item.itemstatus === "cancelled") {
          const key = getItemKey(order._id, item.itemId, idx);
          setDeletedItems((prev) => ({ ...prev, [key]: true }));
        }
      });
    });
  }, [orders]);

  // Order timer
  useEffect(() => {
    const timerId = setInterval(() => {
      const now = Date.now();

      setOrders((prevOrders) =>
        prevOrders.map((order) => {
          // ✅ Skip timer logic for scheduled orders
          if (order.orderStatus === "scheduled") return order;
          if (order.orderStatus === "preparing") {
            const prepTargetSeconds = getPrepTargetSeconds(order);
            const prepStart =
              order.preparingStartedAt ??
              getPreparingTime(order.statusHistory || []) ??
              now;
            const elapsedSeconds = Math.max(
              0,
              Math.floor((now - prepStart) / 1000),
            );

            return {
              ...order,
              showTimer: true,
              prepElapsedSeconds: Math.min(elapsedSeconds, prepTargetSeconds),
              prepTargetSeconds,
            };
          }
          const scheduledTime =
            order.preparingStartedAt ??
            (order.scheduledAt
              ? new Date(order.scheduledAt).getTime()
              : new Date(order.createdAt).getTime());

          // If current time is before scheduled time → not started yet
          if (now < scheduledTime) {
            return {
              ...order,
              showTimer: false,
              isLate: false,
              timeRemaining: Math.max(order.timeRemaining, 0),
            };
          }

          // Time since it was supposed to start
          const elapsedSeconds = Math.floor((now - scheduledTime) / 1000);
          const remaining = Math.max(0, 15 * 60 - elapsedSeconds);
          const isLate = remaining <= 0;

          return {
            ...order,
            showTimer: true,
            timeRemaining: remaining,
            isLate,
            cardBackground: isLate
              ? "#dc2626"
              : order.orderStatus === "received"
                ? "#7CBF80"
                : order.orderStatus === "preparing"
                  ? "#F2B55D"
                  : "",
          };
        }),
      );
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  const formatTime = (time: number): string => {
    const hrs = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = time % 60;
    if (hrs > 0) {
      return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
        2,
        "0",
      )}:${String(secs).padStart(2, "0")}`;
    }
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const getItemKey = (orderId: string, itemId: string, index: number) =>
    `${orderId}-${itemId}-${index}`;

  const performDeleteItem = async (
    orderId: string,
    itemId: string,
    index: number,
  ) => {
    const key = getItemKey(orderId, itemId, index);
    setDeletedItems((prev) => ({ ...prev, [key]: true }));

    try {
      setIsCancellingItem(true);

      const res = await cancelOrderItem(orderId, itemId);

      if (res.data?.success) {
        const updatedOrder = res.data.data;

        setOrders((prev) =>
          prev.map((order) =>
            order._id === orderId
              ? {
                  ...order,
                  ...updatedOrder,
                  orderNumber: updatedOrder?.orderNumber ?? order.orderNumber,
                  timeRemaining: order.timeRemaining,
                }
              : order,
          ),
        );

        // Automatically process refunds after item cancellation
        try {
          await processOrderRefunds(orderId);
          console.log("Refund processed for order:", orderId);
        } catch (refundErr) {
          console.error("Error processing refund:", refundErr);
        }
      } else {
        console.error("Failed to cancel item on server:", res.data?.error);
      }
    } catch (err) {
      console.error("Error cancelling item via API:", err);
    } finally {
      setIsCancellingItem(false);
    }
  };

  const handleDeleteItem = async (
    orderId: string,
    itemId: string,
    index: number,
  ) => {
    if (isCancellingItem) return;

    const order = orders.find((o) => o._id === orderId);
    if (!order) return;

    const key = getItemKey(orderId, itemId, index);
    const item = order.items[index];
    if (!item || deletedItems[key] || item.itemstatus === "cancelled") {
      return;
    }

    // Show passcode modal instead of deleting directly
    setPasscodeInput("");
    setPasscodeError("");
    setPasscodePendingAction({
      type: "deleteItem",
      orderId,
      orderNumber: order.orderNumber,
      itemId,
      index,
      itemName: item.kitchenName || item.name,
    });
  };

  const toggleVisibility = (orderId: string, itemId: string, index: number) => {
    const key = getItemKey(orderId, itemId, index);
    setVisibleItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleStartPreparing = async (orderId: string) => {
    const now = Date.now();

    // Starting prep is the "approval" for a received order — silence its buzzer/highlight now
    // rather than waiting for the next poll to prune it.
    setUnreadRecentOrderIds((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });

    try {
      await updateOrderStatus(orderId, "preparing");
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId
            ? {
                ...order,
                orderStatus: "preparing",
                timeRemaining: 15 * 60,
                preparingStartedAt: now,
                showTimer: true,
                isLate: false,
                prepElapsedSeconds: 0,
                prepTargetSeconds: getPrepTargetSeconds(order),
              }
            : order,
        ),
      );
    } catch (error) {
      console.error("Error updating order status to 'preparing'", error);
    }
  };

  // Acknowledging a scheduled order silences its buzzer/highlight without changing its
  // status — it still converts to "received" on schedule as usual.
  const handleAcceptScheduledOrder = (orderId: string) => {
    setUnreadScheduledOrderIds((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  };

  const handleOpenCancelOrderModal = (order: KitchenOrder) => {
    if (isCancellingOrder) return;
    setPasscodeInput("");
    setPasscodeError("");
    setPasscodePendingAction({
      type: "cancelOrder",
      orderId: order._id,
      orderNumber: order.orderNumber,
    });
  };

  const handlePasscodeSubmit = async () => {
    if (!passcodePendingAction || isVerifyingPasscode) return;
    if (!/^\d{6}$/.test(passcodeInput)) {
      setPasscodeError("Enter a valid 6-digit passcode");
      return;
    }

    setIsVerifyingPasscode(true);
    setPasscodeError("");
    try {
      await verifyEmployeePasscode(user!.restaurantId as string, passcodeInput);
      // Passcode valid – proceed with the pending action
      const action = passcodePendingAction;
      setPasscodePendingAction(null);
      setPasscodeInput("");

      if (action.type === "deleteItem") {
        await performDeleteItem(action.orderId, action.itemId, action.index);
      } else {
        setPendingFullCancelOrder({
          orderId: action.orderId,
          orderNumber: action.orderNumber,
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Invalid passcode";
      setPasscodeInput("");
      setPasscodeError(msg);
      setTimeout(() => passcodeInputRef.current?.focus(), 50);
    } finally {
      setIsVerifyingPasscode(false);
    }
  };

  const closeCancelOrderModal = () => {
    if (isCancellingOrder) return;
    setPendingFullCancelOrder(null);
  };

  const handleCancelFullOrder = async () => {
    if (!pendingFullCancelOrder || isCancellingOrder) return;

    const { orderId } = pendingFullCancelOrder;
    const order = orders.find((o) => o._id === orderId);
    if (!order) {
      setPendingFullCancelOrder(null);
      return;
    }

    try {
      setIsCancellingOrder(true);
      let updatedOrder: Partial<KitchenOrder> | undefined;

      try {
        const cancelResponse = await cancelOrder(orderId);
        if (!cancelResponse.data?.success) {
          throw new Error(cancelResponse.data?.error || "cancelOrder failed");
        }
        updatedOrder = cancelResponse.data.data;
      } catch (cancelApiError) {
        console.warn(
          "cancelOrder API failed, falling back to status update",
          cancelApiError,
        );
        const fallbackResponse = await updateOrderStatus(orderId, "cancelled");
        if (!fallbackResponse.data?.success) {
          throw new Error(
            fallbackResponse.data?.error || "fallback cancellation failed",
          );
        }
        updatedOrder = fallbackResponse.data.data;
      }

      const deletedItemMap = order.items.reduce(
        (acc, item, idx) => {
          const key = getItemKey(orderId, item.itemId, idx);
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setDeletedItems((prev) => ({ ...prev, ...deletedItemMap }));
      setOrders((prev) =>
        prev.map((o) =>
          o._id === orderId
            ? {
                ...o,
                ...updatedOrder,
                orderNumber: updatedOrder?.orderNumber ?? o.orderNumber,
                orderStatus: "cancelled",
              }
            : o,
        ),
      );
      setPendingFullCancelOrder(null);

      // Automatically process refunds after order cancellation
      try {
        await processOrderRefunds(orderId);
        console.log("Refund processed for cancelled order:", orderId);
      } catch (refundErr) {
        console.error("Error processing refund for order:", refundErr);
      }
    } catch (error) {
      console.error("Error cancelling full order", error);
    } finally {
      setIsCancellingOrder(false);
    }
  };

  const handleMarkAsDone = async (orderId: string) => {
    const order = orders.find((o) => o._id === orderId);
    if (!order) return;

    setVisibleItems((prev) => ({
      ...prev,
      ...order.items.reduce(
        (acc, item, idx) => {
          const key = getItemKey(orderId, item.itemId, idx);
          if (!deletedItems[key]) acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    }));

    const now = Date.now();
    const prepSeconds =
      getPrepTimeSeconds(order, now) ??
      (order.preparingStartedAt
        ? Math.max(0, Math.round((now - order.preparingStartedAt) / 1000))
        : order.timeRemaining ?? 0);
    setDoneMeta((prev) => ({
      ...prev,
      [orderId]: {
        prepTimeSeconds: prepSeconds,
        readyAt: now + DEFAULT_READY_SECONDS * 1000,
        markedAt: now,
      },
    }));

    setOrders((prev) =>
      prev.map((o) => (o._id === orderId ? { ...o, orderStatus: "ready" } : o)),
    );

    try {
      await updateOrderStatus(orderId, "ready");
    } catch (error) {
      console.error("Error updating order status to 'ready'", error);
    }
  };

  const handleMarkAsPickedUp = async (orderId: string) => {
    const pickedUpAt = new Date();

    setOrders((prevOrders) =>
      prevOrders.map((order) => {
        if (order._id !== orderId) return order;
        const statusHistory = Array.isArray(order.statusHistory)
          ? [...order.statusHistory]
          : [];
        statusHistory.push({
          status: "pickedup",
          updatedAt: pickedUpAt,
        });
        return {
          ...order,
          orderStatus: "pickedup",
          statusHistory,
        };
      }),
    );

    try {
      await updateOrderStatus(orderId, "pickedup");
    } catch (error) {
      console.error("Error updating order status to 'pickedup'", error);
    }
  };

  const isMarkAsDoneDisabled = (order: KitchenOrder) => {
    return order.items.some((item, idx) => {
      const key = getItemKey(order._id, item.itemId, idx);
      return !deletedItems[key] && !visibleItems[key];
    });
  };

  const isOrderCancelled = (order: KitchenOrder) => {
    const itemsDeleted = order.items.every((item, idx) => {
      const key = getItemKey(order._id, item.itemId, idx);
      return deletedItems[key];
    });

    return (
      order.orderStatus === "cancelled" ||
      order.orderStatus === "partiallycancelled" ||
      itemsDeleted
    );
  };

  // Countdown
  const CountdownTo: React.FC<{
    targetTs: number;
    className?: string;
    lateClassName?: string;
  }> = ({ targetTs, className, lateClassName }) => {
    const [secLeft, setSecLeft] = useState(
      Math.max(0, Math.ceil((targetTs - Date.now()) / 1000)),
    );

    useEffect(() => {
      const id = setInterval(() => {
        setSecLeft(Math.max(0, Math.ceil((targetTs - Date.now()) / 1000)));
      }, 1000);
      return () => clearInterval(id);
    }, [targetTs]);

    if (secLeft <= 0) {
      return (
        <span className={lateClassName || "text-white font-bold"}>
          Order Late
        </span>
      );
    }
    return <span className={className}>{formatTime(secLeft)}</span>;
  };

  // Post cancelled status if all items deleted
  useEffect(() => {
    orders.forEach(async (order) => {
      const cancelledByItems = order.items.every((item, idx) => {
        const key = getItemKey(order._id, item.itemId, idx);
        return deletedItems[key];
      });

      if (cancelledByItems && order.orderStatus !== "cancelled") {
        try {
          await updateOrderStatus(order._id, "cancelled");
          setOrders((prev) =>
            prev.map((o) =>
              o._id === order._id ? { ...o, orderStatus: "cancelled" } : o,
            ),
          );
        } catch (err) {
          console.error("Failed to update order to cancelled", err);
        }
      }
    });
  }, [orders, deletedItems]);

  // Done card
  const DoneCard: React.FC<{ order: KitchenOrder; meta: DoneMeta }> = ({
    order,
    meta,
  }) => {
    const orderTimezone = resolveOrderTimezone(order);
    return (
      <div
        key={order._id}
        className="w-80 mt-3 rounded-xl overflow-hidden font-sans shadow-sm bg-[#0f4258] text-white p-3"
      >
        <div className="flex justify-between items-start border-b border-white">
          <div>
            <div className="text-sm font-outfit font-semibold">
              {order.tableToken ? `Table#${order.tableToken}` : "Pickup"}
            </div>

            <div className="text-xs font-semibold font-outfit text-white/70">
              {formatOrderTime(order.createdAt, orderTimezone)}
            </div>
          </div>

          <div className="text-right">
            <div className="font-outfit font-semibold">
              {getOrderNumberLabel(order)}
            </div>
            <div className="text-xs font-semibold text-outfit text-white/70">
              <span className="mr-1 ">⏱</span>
              <CountdownTo targetTs={meta.readyAt} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <div className="rounded-full bg-[#6CB670] p-2">
            {order.choice?.toLowerCase() === "dining in" ? (
              <img src={spoon} alt="dine-in" className="w-5 h-5" />
            ) : order.choice?.toLowerCase() === "takeaway" ? (
              <img src={box} alt="takeaway" className="w-5 h-5" />
            ) : order.choice?.toLowerCase() === "online" ? (
              <img src={online} alt="online" className="w-5 h-5" />
            ) : order.choice?.toLowerCase() === "curbside" ? (
              <img src={curbside} alt="curbside" className="w-5 h-5" />
            ) : (
              <img src={counter} alt="counter" className="w-5 h-5" />
            )}
          </div>
          <div className="text-sm font-outfit">
            {formatChoiceLabel(order.choice)}
          </div>

          <div
            onClick={triggerNotification}
            className="bg-[#c7d8e069] text-white px-2 py-1 rounded-full text-xs ml-auto flex gap-1"
          >
            <img
              src={call}
              alt="Logo"
              className="w-[15px] h-[15px] bg-white rounded-full cursor-pointer"
            />
            <span className="cusor-pointer">Buzzer</span>
          </div>
        </div>

        <div className="mt-4 text-white/90 max-h-52 overflow-y-auto no-scrollbar">
          {order.items.map((item, idx) => {
            const key = getItemKey(order._id, item.itemId, idx);
            if (deletedItems[key]) return null;
            return (
              <div key={key} className="mb-2">
                <div className="text-lg font-semibold">
                  {item.quantity} x {item.kitchenName || item.name}
                </div>
                <div className="text-xs text-white/70">
                  ⏱ {Math.round(meta.prepTimeSeconds / 60)} min
                </div>
                <ItemCustomizations
                  item={item}
                  textClassName="text-white/70"
                  showInstructions={false}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderOrderCard = (order: KitchenOrder) => {
    const orderTimezone = resolveOrderTimezone(order);
    // ✅ Render scheduled card
    if (order.orderStatus === "scheduled") {
      return (
        <ScheduledCard
          key={order._id}
          order={order}
          deletedItems={deletedItems}
          getItemKey={getItemKey}
          formatTime={formatTime}
          onDeleteItem={handleDeleteItem}
          onOpenDelayModal={openDelayScheduleModal}
          onCancelOrder={handleOpenCancelOrderModal}
          isCancellingOrder={isCancellingOrder}
        />
      );
    }

    if (order.orderStatus === "ready" && doneMeta[order._id]) {
      return (
        <DoneCard key={order._id} order={order} meta={doneMeta[order._id]} />
      );
    }

    return (
      <div
        key={order._id}
        className="w-80 h-[430px] mt-3 border border-gray-200 rounded-xl overflow-hidden font-sans shadow-sm bg-white flex flex-col"
      >
        <div
          style={{
            backgroundColor:
              isOrderCancelled(order) || order.isLate
                ? "#dc2626"
                : order.cardBackground,
          }}
          className="p-2 text-white text-sm"
        >
          <div className="flex justify-between">
            <span className="text-right font-bold font-outfit">
              {getOrderNumberLabel(order)}
            </span>

            <span className="font-outfit">
              <span className="font-bold">Order By:</span>{" "}
              <span>{order.phoneNumber}</span>
            </span>
          </div>
          <div className="flex font-semibold justify-between">
            {order.choice && (
              <div className="flex items-center gap-2">
                <span className="text-s font-outfit text-white/80">
                  {formatChoiceLabel(order.choice)}
                </span>
                {order.choice.toLowerCase() === "dining in" && (
                  <img
                    src={spoon}
                    alt="Dining In"
                    className="w-[20px] h-[20px]"
                  />
                )}
                {order.choice.toLowerCase() === "takeaway" && (
                  <img src={box} alt="Takeaway" className="w-[20px] h-[20px]" />
                )}
                {order.choice.toLowerCase() === "online" && (
                  <img
                    src={online}
                    alt="Online"
                    className="w-[20px] h-[20px]"
                  />
                )}
                {order.choice.toLowerCase() === "curbside" && (
                  <img
                    src={curbside}
                    alt="Curbside"
                    className="w-[20px] h-[20px]"
                  />
                )}
              </div>
            )}
            {order.showTimer ? (
              <span
                className={`text-right font-outfit ${
                  order.isLate ? "text-white font-bold" : ""
                }`}
              >
                {isOrderCancelled(order)
                  ? "Cancelled"
                  : order.isLate
                    ? "Order Late"
                    : formatTime(order.timeRemaining)}
              </span>
            ) : (
              <div>
                {order.scheduledAt && (
                  <p className="font-outfit text-white mb-0">
                    Scheduled for:{" "}
                    {formatScheduledDateTime(order.scheduledAt, orderTimezone)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 no-scrollbar">
          {order.items.map((item: OrderItem, idx: number) => {
            const key = getItemKey(order._id, item.itemId, idx);
            return (
              <SwipeItem
                key={key}
                onDone={() => toggleVisibility(order._id, item.itemId, idx)}
                onDelete={() => handleDeleteItem(order._id, item.itemId, idx)}
                isPreparing={order.orderStatus === "preparing"}
              >
                <div
                  className={`p-4 border-b border-gray-200 text-sm flex justify-between items-start gap-2 ${
                    deletedItems[key] || item.itemstatus === "cancelled"
                      ? "line-through text-red-600"
                      : ""
                  }`}
                >
                  <div
                    className={`font-outfit flex-1 ${
                      deletedItems[key] ||
                      order.orderStatus === "cancelled" ||
                      item.itemstatus === "cancelled"
                        ? "line-through text-red-500"
                        : "text-black"
                    }`}
                  >
                    <b>{item.quantity}x</b> {item.kitchenName || item.name}
                    <ItemCustomizations item={item} />
                  </div>

                  <button
                    onClick={(e) => e.stopPropagation()}
                    className={`w-[18px] h-[18px] min-w-[18px] min-h-[18px] self-start mt-0.5 border border-black rounded-full flex items-center justify-center ${
                      order.orderStatus !== "preparing" || deletedItems[key]
                        ? "opacity-40 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={
                      order.orderStatus !== "preparing" || deletedItems[key]
                    }
                  >
                    {visibleItems[key] &&
                      !deletedItems[key] &&
                      order.orderStatus === "preparing" && (
                        <img
                          src={check}
                          alt="Logo"
                          className="w-[18px] h-[18px]"
                        />
                      )}
                  </button>
                </div>
              </SwipeItem>
            );
          })}
        </div>
        <div className="mt-auto border-t border-gray-100">
          <div className="py-2 flex justify-center">
            {order.orderStatus === "received" ? (
              <div className="w-11/12 flex flex-col gap-2">
                <button
                  onClick={() => handleStartPreparing(order._id)}
                  className="w-full py-2 border border-black rounded-lg bg-white text-sm font-medium font-outfit cursor-pointer"
                  disabled={isOrderCancelled(order)}
                >
                  Start Preparing
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenCancelOrderModal(order)}
                  className="w-full py-2 border border-red-600 text-red-600 rounded-lg bg-white text-sm font-medium font-outfit disabled:opacity-50"
                  disabled={isOrderCancelled(order) || isCancellingOrder}
                >
                  Cancel Order
                </button>
              </div>
            ) : order.orderStatus === "preparing" ? (
              <button
                onClick={() => handleMarkAsDone(order._id)}
                className="w-11/12 py-2 border font-outfit border-black rounded-lg  text-black text-sm font-medium cursor-pointer"
                disabled={
                  isMarkAsDoneDisabled(order) || isOrderCancelled(order)
                }
              >
                Mark as Done
              </button>
            ) : null}
          </div>
          <div className="px-3 py-2 text-xs text-black font-outfit bg-white w-full">
            Ordered at: {formatOrderTime(order.createdAt, orderTimezone)}
          </div>
        </div>
      </div>
    );
  };

  const toggleOrderRow = (orderId: string) => {
    setExpandedOrderRows((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };
  const openOrderDetail = (order: KitchenOrder) => {
    setSelectedOrder(order);
    // Received/scheduled orders stay flagged (buzzer + highlight) until explicitly
    // approved (Start Preparing / Accept) — merely viewing the order doesn't clear them.
    setUnreadDineInOrderIds((prev) => {
      if (!prev.has(order._id)) return prev;
      const next = new Set(prev);
      next.delete(order._id);
      return next;
    });
  };
  const closeOrderDetail = () => {
    setSelectedOrder(null);
  };
  const updateSelectedOrder = (
    orderId: string,
    patch: Partial<KitchenOrder>,
  ) => {
    setSelectedOrder((prev) =>
      prev && prev._id === orderId ? { ...prev, ...patch } : prev,
    );
  };

  const renderOrderActionButtons = (
    order: KitchenOrder,
    variant: "row" | "modal",
  ) => {
    const isCancelled = isOrderCancelled(order);
    const canMarkPickedUp =
      order.orderStatus === "ready" && !hasDropoffAddressInMetadata(order);
    const baseClass =
      variant === "modal"
        ? "h-9 px-4 min-w-[120px] rounded-lg border text-sm font-outfit disabled:opacity-40"
        : "h-8 w-[96px] sm:w-[104px] lg:w-[112px] rounded-lg border text-xs font-outfit disabled:opacity-40";
    const stopProp = variant === "row";
    const wrapClick =
      (handler: () => void) => (event: React.MouseEvent<HTMLButtonElement>) => {
        if (stopProp) event.stopPropagation();
        handler();
      };

    return (
      <>
        {order.orderStatus === "received" && (
          <button
            onClick={wrapClick(() => handleStartPreparing(order._id))}
            className={`border-slate-300 hover:bg-slate-50 ${baseClass}`}
            disabled={isCancelled}
          >
            Start
          </button>
        )}
        {order.orderStatus === "preparing" && (
          <button
            onClick={wrapClick(() => handleMarkAsDone(order._id))}
            className={`border-slate-300 hover:bg-slate-50 ${baseClass}`}
            disabled={isMarkAsDoneDisabled(order) || isCancelled}
          >
            Mark Done
          </button>
        )}
        {order.orderStatus === "scheduled" &&
          unreadScheduledOrderIds.has(order._id) && (
            <button
              type="button"
              onClick={wrapClick(() => handleAcceptScheduledOrder(order._id))}
              className={`border-amber-400 text-amber-700 hover:bg-amber-50 ${baseClass}`}
            >
              Accept
            </button>
          )}
        {order.orderStatus === "scheduled" && (
          <button
            type="button"
            onClick={wrapClick(() => openDelayScheduleModal(order))}
            className={`border-slate-300 hover:bg-slate-50 ${baseClass}`}
          >
            Delay
          </button>
        )}
        {order.orderStatus === "ready" && (
          <>
            <button
              onClick={wrapClick(triggerNotification)}
              className={`border-slate-300 hover:bg-slate-50 ${baseClass} flex items-center justify-center gap-1`}
            >
              <img
                src={call}
                alt="Call"
                className="w-3.5 h-3.5 rounded-full bg-white"
              />
              Buzzer
            </button>
            {canMarkPickedUp && (
              <button
                type="button"
                onClick={wrapClick(() => handleMarkAsPickedUp(order._id))}
                className={`border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${baseClass}`}
              >
                Picked Up
              </button>
            )}
          </>
        )}
        {!isCancelled &&
          (order.orderStatus === "received" ||
            order.orderStatus === "preparing" ||
            order.orderStatus === "scheduled") && (
            <button
              type="button"
              onClick={wrapClick(() => handleOpenCancelOrderModal(order))}
              className={`border-red-200 text-red-600 hover:bg-red-50 ${baseClass}`}
              disabled={isCancellingOrder}
            >
              Cancel
            </button>
          )}
      </>
    );
  };

  const hasDeliveryStatusHistory = (order: KitchenOrder) =>
    hasDropoffAddressInMetadata(order) &&
    (Boolean(order.deliveryStatus) ||
      (Array.isArray(order.deliveryStatusHistory) &&
        order.deliveryStatusHistory.length > 0));

  const renderOrderRow = (order: KitchenOrder) => {
    const orderTimezone = resolveOrderTimezone(order);
    const isHistoryView = activeView === "history";
    const isExpanded = !!expandedOrderRows[order._id];
    const pickupDeadline =
      order.orderStatus === "ready" ? getPickupDeadline(order) : undefined;
    const completedAt =
      order.orderStatus === "ready"
        ? getReadyCompletedTime(order.statusHistory || []) ??
          doneMeta[order._id]?.markedAt
        : undefined;
    const prepTimeSeconds =
      order.orderStatus === "ready"
        ? getPrepTimeSeconds(order, completedAt)
        : undefined;
    const isPrepLate =
      order.orderStatus === "preparing" &&
      order.prepElapsedSeconds != null &&
      order.prepTargetSeconds != null &&
      order.prepElapsedSeconds >= order.prepTargetSeconds;
    const normalizedDeliveryStatus = getLatestNormalizedDeliveryStatus(order);
    const hasDeliveryStatus = hasDeliveryStatusHistory(order);
    const deliveryStatusLabelMap: Record<string, string> = {
      pending: "Pending",
      pickup: "Pickup",
      pickup_complete: "Pickup Complete",
      dropoff: "Dropoff",
      delivered: "Delivered",
      cancelled: "Cancelled",
    };
    const deliveryStatusLabel =
      deliveryStatusLabelMap[normalizedDeliveryStatus] ||
      normalizedDeliveryStatus
        .split("_")
        .map((segment) =>
          segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : "",
        )
        .join(" ");
    const deliveryStatusClass =
      normalizedDeliveryStatus === "delivered"
        ? "bg-emerald-100 text-emerald-700"
        : normalizedDeliveryStatus === "dropoff"
          ? "bg-indigo-100 text-indigo-700"
          : normalizedDeliveryStatus === "pickup" ||
              normalizedDeliveryStatus === "pickup_complete"
            ? "bg-sky-100 text-sky-700"
            : normalizedDeliveryStatus === "cancelled"
              ? "bg-red-100 text-red-700"
              : "bg-slate-100 text-slate-700";
    const activeItemsCount = order.items.filter((item, idx) => {
      const key = getItemKey(order._id, item.itemId, idx);
      return !deletedItems[key] && item.itemstatus !== "cancelled";
    }).length;
    const originalItemQuantityCount = order.items.reduce(
      (sum, item) =>
        sum +
        (typeof item.quantity === "number" && item.quantity > 0
          ? item.quantity
          : 0),
      0,
    );
    const displayedItemsCount = isHistoryView
      ? originalItemQuantityCount > 0
        ? originalItemQuantityCount
        : order.items.length
      : activeItemsCount;
    const totalAmountLabel =
      typeof order.totalAmount === "number"
        ? `$${order.totalAmount.toFixed(2)}`
        : "--";
    const originalTotalAmount = calculateOriginalOrderTotal(order);
    const showCancelledOriginalTotal =
      isHistoryView &&
      order.orderStatus?.toLowerCase() === "cancelled" &&
      typeof order.totalAmount === "number" &&
      order.totalAmount <= 0 &&
      originalTotalAmount > 0;

    const isCancelled = isOrderCancelled(order);
    const statusLabel = isCancelled
      ? "Cancelled"
      : order.orderStatus === "scheduled"
        ? "Scheduled"
        : order.orderStatus === "pickedup"
          ? "Picked Up"
        : order.orderStatus === "preparing"
          ? "Preparing"
          : order.orderStatus === "ready"
            ? "Completed"
            : "Received";
    const statusPillClass = isCancelled
      ? "bg-red-100 text-red-700"
      : order.orderStatus === "scheduled"
        ? "bg-slate-100 text-slate-700"
        : order.orderStatus === "pickedup"
          ? "bg-teal-100 text-teal-700"
        : order.orderStatus === "preparing"
          ? "bg-amber-100 text-amber-700"
          : order.orderStatus === "ready"
            ? "bg-emerald-100 text-emerald-700"
            : "bg-blue-100 text-blue-700";
    const statusMetaText =
      order.orderStatus === "scheduled"
        ? formatScheduledDateTime(order.scheduledAt, orderTimezone)
        : `Ordered at ${formatOrderTime(order.createdAt, orderTimezone)}`;
    const needsApproval =
      !isHistoryView &&
      ((order.orderStatus === "received" &&
        unreadRecentOrderIds.has(order._id)) ||
        (order.orderStatus === "scheduled" &&
          unreadScheduledOrderIds.has(order._id)));

    return (
      <div
        key={order._id}
        className={`w-full rounded-2xl border overflow-hidden shadow-sm ${
          needsApproval
            ? "border-amber-400 ring-2 ring-amber-300 bg-amber-50"
            : "border-slate-200 bg-white"
        }`}
      >
        <div
          onClick={() => openOrderDetail(order)}
          className={`grid ${ORDERS_ROW_COLUMNS_CLASS} min-w-[600px] sm:min-w-[660px] md:min-w-0 gap-1.5 lg:gap-2 xl:gap-3 px-3 lg:px-4 py-3 items-center min-h-[92px] cursor-pointer`}
        >
          <div className="min-w-0">
            <p className="text-lg font-semibold font-outfit text-slate-500 mt-0.5 mb-0 truncate flex items-center gap-1.5">
              {getOrderNumberLabel(order)}
              {needsApproval && (
                <span className="inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white animate-pulse">
                  NEW
                </span>
              )}
            </p>
             <p className="text-base font-semibold font-outfit text-slate-500 m-0 truncate">
              • {formatChoiceLabel(order.choice)}
            </p>
          </div>

          <div className="min-w-0">
            {showCancelledOriginalTotal ? (
              <p className="text-base m-0 font-semibold font-outfit text-slate-500 line-through">
                ${originalTotalAmount.toFixed(2)}
              </p>
            ) : (
              <p className="text-base m-0 font-semibold font-outfit text-slate-900">
                {totalAmountLabel}
              </p>
            )}
            <p className="text-xs font-outfit text-slate-500">
              {displayedItemsCount} item{displayedItemsCount === 1 ? "" : "s"}
            </p>
          </div>

          <div className="min-w-0">
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold font-outfit ${statusPillClass}`}
            >
              {statusLabel}
            </span>
            <p className="text-xs font-outfit text-slate-500 mt-1 mb-1 whitespace-nowrap overflow-hidden text-ellipsis block">
              {order.scheduledAt && order.orderStatus !== "scheduled"
                ? `Scheduled for: ${formatScheduledDateTime(
                    order.scheduledAt,
                    orderTimezone,
                  )}`
                : statusMetaText}
              {order.orderStatus === "preparing" &&
                order.prepElapsedSeconds != null && (
                <>
                  {isPrepLate ? (
                    <span className="text-red-600 font-semibold">
                      {" • Order Late"}
                    </span>
                  ) : (
                    <span className="text-slate-600">
                      {" • Preparing: "}
                      {formatTime(order.prepElapsedSeconds)}
                    </span>
                  )}
                </>
              )}
            </p>
            {order.orderStatus === "ready" &&
              (pickupDeadline || prepTimeSeconds != null) && (
                <p className="text-xs font-outfit text-slate-500 mt-0 mb-0 whitespace-nowrap overflow-hidden text-ellipsis block">
                  {prepTimeSeconds != null && (
                    <>Prepared in: {formatTime(prepTimeSeconds)}</>
                  )}
                  {pickupDeadline && prepTimeSeconds != null && " • "}
                  {pickupDeadline && (
                    <>
                      {"Pickup: "}
                      <CountdownTo
                        targetTs={pickupDeadline}
                        className="text-slate-600"
                        lateClassName="text-red-600 font-semibold"
                      />
                    </>
                  )}
                </p>
              )}
            {order.orderStatus !== "scheduled" &&
              order.orderStatus !== "ready" &&
              order.scheduledAt && (
                <p className="text-xs font-outfit text-slate-500 mt-0 mb-0 whitespace-nowrap overflow-hidden text-ellipsis block">
                  {statusMetaText}
                </p>
              )}
            {hasDeliveryStatus && (
              <div>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold font-outfit ${deliveryStatusClass}`}
                >
                  Delivery: {deliveryStatusLabel}
                </span>
              </div>
            )}
          </div>

          <div
            className="flex items-center gap-1 md:gap-1.5 xl:gap-2 flex-wrap lg:flex-nowrap justify-end w-full"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {!isHistoryView && renderOrderActionButtons(order, "row")}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleOrderRow(order._id);
            }}
            className="w-7 h-7 lg:w-8 lg:h-8 xl:w-9 xl:h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"
            aria-label={isExpanded ? "Collapse order" : "Expand order"}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>

        <div
          className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
            isExpanded ? "max-h-[2000px]" : "max-h-0 pointer-events-none"
          }`}
        >
          <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60">
            <div className="space-y-2">
              {order.items.map((item: OrderItem, idx: number) => {
                const key = getItemKey(order._id, item.itemId, idx);
                const isDeleted =
                  deletedItems[key] ||
                  item.itemstatus === "cancelled" ||
                  order.orderStatus === "cancelled";
                return (
                  <div
                    key={key}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 flex items-start gap-2"
                  >
                    <div
                      className={`font-outfit text-sm flex-1 ${
                        isDeleted ? "line-through text-red-500" : "text-slate-900"
                      }`}
                    >
                      <b>{item.quantity}x</b> {item.kitchenName || item.name}
                      <ItemCustomizations item={item} />
                    </div>

                    {!isHistoryView && (
                      <div className="flex items-center gap-2">
                        {order.orderStatus === "preparing" && (
                          <button
                            type="button"
                            onClick={() =>
                              toggleVisibility(order._id, item.itemId, idx)
                            }
                            className={`w-[20px] h-[20px] min-w-[20px] min-h-[20px] border border-black rounded-full flex items-center justify-center ${
                              isDeleted ? "opacity-40 cursor-not-allowed" : ""
                            }`}
                            disabled={isDeleted}
                          >
                            {visibleItems[key] && !isDeleted && (
                              <img
                                src={check}
                                alt="Done"
                                className="w-[18px] h-[18px]"
                              />
                            )}
                          </button>
                        )}
                        {!isDeleted && (
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteItem(order._id, item.itemId, idx)
                            }
                            className="px-2 py-1 rounded-md text-xs border border-red-200 text-red-600 hover:bg-red-50 font-outfit"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isCategoryEnabled = (choice?: string) => {
    if (!choice)
      return settings.find((s) => s.name === "CounterSide")?.enabled ?? true;
    if (choice.toLowerCase() === "dining in")
      return settings.find((s) => s.name === "Dining in")?.enabled ?? true;
    if (choice.toLowerCase() === "takeaway")
      return settings.find((s) => s.name === "Takeaway")?.enabled ?? true;
    if (choice.toLowerCase() === "online")
      return settings.find((s) => s.name === "Online")?.enabled ?? true;
    if (choice.toLowerCase() === "curbside")
      return settings.find((s) => s.name === "CurbSide")?.enabled ?? true;
    return true;
  };

  const filterOrdersForView = (ordersList: KitchenOrder[]) => {
    const baseOrders =
      activeView === "history"
        ? ordersList
        : ordersList.filter((o) => isCategoryEnabled(o.choice));

    const viewFilteredOrders =
      activeView === "history"
        ? baseOrders
        : activeView === "scheduled"
          ? baseOrders.filter(
              (o) =>
                o.orderStatus === "scheduled" &&
                new Date(o.scheduledAt ?? 0).getTime() > Date.now(),
            )
          : activeView === "dinein"
            ? baseOrders.filter(
                (o) =>
                  o.choice?.toLowerCase() === "dining in" &&
                  o.orderStatus !== "cancelled",
              )
            : baseOrders.filter(
                (o) =>
                  o.orderStatus !== "scheduled" &&
                  o.orderStatus !== "cancelled" &&
                  o.orderStatus !== "delivered" &&
                  !isDeliveryClosedOrder(o) &&
                  shouldShowPickedUpInRecent(o),
              );

    const normalizedHistorySearch = historySearchTerm.trim().toLowerCase();
    const searchedOrders =
      activeView !== "history" || !normalizedHistorySearch
        ? viewFilteredOrders
        : viewFilteredOrders.filter((order) => {
            const orderNumber = String(order.orderNumber || "").toLowerCase();
            const fallbackOrderNumber = order._id.slice(-6).toLowerCase();
            const phoneNumber =
              order.phoneNumber != null
                ? String(order.phoneNumber).toLowerCase()
                : "";
            return (
              orderNumber.includes(normalizedHistorySearch) ||
              fallbackOrderNumber.includes(normalizedHistorySearch) ||
              phoneNumber.includes(normalizedHistorySearch)
            );
          });

    return [...searchedOrders].sort((a, b) =>
      sortOrder === "older"
        ? getOrderSortTime(a) - getOrderSortTime(b)
        : getOrderSortTime(b) - getOrderSortTime(a),
    );
  };

  const filteredOrders = filterOrdersForView(orders);
  const renderedOrders = filteredOrders.map((order) => renderOrderRow(order));
  const activeViewLabel =
    activeView === "history"
      ? "Order History"
      : activeView === "scheduled"
      ? "Scheduled Orders"
      : activeView === "dinein"
        ? "Dine-in Orders"
        : "Recent Orders";
  const unseenRecentOrderCount = unreadRecentOrderIds.size;
  const unseenScheduledOrderCount = unreadScheduledOrderIds.size;
  const unseenDineInOrderCount = unreadDineInOrderIds.size;

  useEffect(() => {
    setVisibleOrderCount(ORDERS_BATCH_SIZE);
    if (ordersScrollRef.current) {
      ordersScrollRef.current.scrollTop = 0;
    }
  }, [activeView, sortOrder]);

  useEffect(() => {
    // Received/scheduled unread flags are intentionally NOT cleared just by viewing
    // this tab — they only clear via explicit approval (Start Preparing / Accept), or
    // via the pruning above once the order's status/eligibility actually changes.

    if (activeView === "dinein") {
      setUnreadDineInOrderIds((prev) => {
        if (prev.size === 0) return prev;
        const dineInOrderIds = new Set(
          orders
            .filter(
              (order) =>
                order.choice?.toLowerCase() === "dining in" &&
                order.orderStatus !== "cancelled",
            )
            .map((order) => order._id),
        );
        const next = new Set(
          Array.from(prev).filter((orderId) => !dineInOrderIds.has(orderId)),
        );
        return next.size === prev.size ? prev : next;
      });
    }
  }, [activeView, orders]);

  // --- Scroll batch logic (13 rows at a time) ---
  const currentOrders = renderedOrders.slice(0, visibleOrderCount);
  const delayedSchedulePreview = scheduleModalOrder
    ? new Date(
        getOrderScheduleBaseTime(scheduleModalOrder) + delayMinutes * 60 * 1000,
      )
    : null;
  const delayedScheduleTimezone = scheduleModalOrder
    ? resolveOrderTimezone(scheduleModalOrder)
    : undefined;
  const selectedOrderTimezone = selectedOrder
    ? resolveOrderTimezone(selectedOrder)
    : undefined;
  const selectedOrderPhoneRaw =
    selectedOrder?.phoneNumber != null
      ? String(selectedOrder.phoneNumber).trim()
      : "";
  const selectedOrderPhoneHref = selectedOrderPhoneRaw
    ? `tel:${selectedOrderPhoneRaw.replace(/(?!^\+)[^0-9]/g, "")}`
    : "";
  const deliveryTimelineSteps = [
    { key: "pending", label: "Pending" },
    { key: "pickup", label: "Pickup" },
    { key: "pickup_complete", label: "Pickup Complete" },
    { key: "dropoff", label: "Dropoff" },
    { key: "delivered", label: "Delivered" },
  ];

  const buildDeliveryStatusTimeline = (order: KitchenOrder) => {
    const historyMap = new Map<string, string | Date>();
    const orderedHistoryStatuses: string[] = [];
    if (Array.isArray(order.deliveryStatusHistory)) {
      order.deliveryStatusHistory.forEach((entry) => {
        const statusKey =
          typeof entry?.status === "string"
            ? entry.status.trim().toLowerCase()
            : "";
        if (!statusKey) return;
        const normalizedUpdatedAt = normalizeStatusTimestamp(entry.updatedAt);
        if (normalizedUpdatedAt) {
          historyMap.set(statusKey, normalizedUpdatedAt);
        }
        orderedHistoryStatuses.push(statusKey);
      });
    }

    const steps = deliveryTimelineSteps.map((step) => {
      const updatedAt = historyMap.get(step.key);
      const timeLabel = updatedAt
        ? formatOrderTime(updatedAt, resolveOrderTimezone(order))
        : "--";
      return { ...step, timeLabel };
    });

    const normalizedDeliveryStatus =
      typeof order.deliveryStatus === "string"
        ? order.deliveryStatus.trim().toLowerCase()
        : "";
    const fallbackStatusKey =
      orderedHistoryStatuses[orderedHistoryStatuses.length - 1] || "";

    let activeIndex = steps.findIndex(
      (step) => step.key === (normalizedDeliveryStatus || fallbackStatusKey),
    );
    if (activeIndex < 0) {
      activeIndex = steps.reduce(
        (acc, step, idx) => (historyMap.has(step.key) ? idx : acc),
        0,
      );
    }

    return { steps, activeIndex: Math.max(activeIndex, 0) };
  };

  const buildStatusTimeline = (order: KitchenOrder) => {
    const historyMap = new Map<string, string | Date>();
    if (Array.isArray(order.statusHistory)) {
      order.statusHistory.forEach((entry) => {
        if (entry?.status) {
          const normalizedStatus = entry.status.trim().toLowerCase();
          const statusKey =
            normalizedStatus === "completed" ||
            normalizedStatus === "complete" ||
            normalizedStatus === "done"
              ? "ready"
              : normalizedStatus;
          const normalizedUpdatedAt = normalizeStatusTimestamp(entry.updatedAt);
          if (normalizedUpdatedAt) {
            historyMap.set(statusKey, normalizedUpdatedAt);
          }
        }
      });
    }

    const baseSteps = [
      { key: "scheduled", label: "Scheduled" },
      { key: "received", label: "Received" },
      { key: "preparing", label: "Preparing" },
      { key: "ready", label: "Completed" },
      { key: "pickedup", label: "Picked Up" },
    ];

    const steps = baseSteps.map((step) => {
      let updatedAt = historyMap.get(step.key);
      if (!updatedAt) {
        if (step.key === "scheduled") updatedAt = order.scheduledAt;
        if (step.key === "received") updatedAt = order.createdAt;
        if (step.key === "ready") {
          const markedAt =
            getReadyCompletedTime(order.statusHistory || []) ??
            doneMeta[order._id]?.markedAt;
          updatedAt = markedAt ? new Date(markedAt) : undefined;
        }
        if (step.key === "pickedup") {
          const pickedUpAt = getPickedUpTime(order.statusHistory || []);
          updatedAt = pickedUpAt ? new Date(pickedUpAt) : undefined;
        }
      }
      const timeLabel = updatedAt
        ? formatOrderTime(updatedAt, resolveOrderTimezone(order))
        : "--";
      return { ...step, timeLabel };
    });

    let activeIndex = baseSteps.findIndex(
      (step) => step.key === order.orderStatus,
    );
    if (activeIndex < 0) {
      activeIndex = steps.reduce(
        (acc, step, idx) => (historyMap.has(step.key) ? idx : acc),
        0,
      );
    }

    return { steps, activeIndex };
  };

  const getTimelineProgressStyle = (activeIndex: number, totalSteps: number) => {
    if (totalSteps <= 0) {
      return { left: "0%", width: "0%" };
    }

    const safeActive = Math.min(Math.max(activeIndex, 0), totalSteps - 1);
    const leftPercent = 50 / totalSteps;
    const widthPercent = (safeActive / totalSteps) * 100;

    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
    };
  };

  const selectedOrderTimeline = selectedOrder
    ? buildStatusTimeline(selectedOrder)
    : null;
  const selectedOrderPickupDeadline =
    selectedOrder?.orderStatus === "ready"
      ? getPickupDeadline(selectedOrder)
      : undefined;
  const selectedOrderCompletedAt =
    selectedOrder?.orderStatus === "ready"
      ? getReadyCompletedTime(selectedOrder.statusHistory || []) ??
        doneMeta[selectedOrder._id]?.markedAt
      : undefined;
  const selectedOrderPrepTimeSeconds =
    selectedOrder?.orderStatus === "ready"
      ? getPrepTimeSeconds(selectedOrder, selectedOrderCompletedAt)
      : undefined;
  const selectedOrderPrepLate =
    selectedOrder?.orderStatus === "preparing" &&
    selectedOrder.prepElapsedSeconds != null &&
    selectedOrder.prepTargetSeconds != null &&
    selectedOrder.prepElapsedSeconds >= selectedOrder.prepTargetSeconds;
  const selectedOrderPrepCountdown =
    selectedOrder?.orderStatus === "preparing" &&
    selectedOrder.prepElapsedSeconds != null
      ? formatTime(selectedOrder.prepElapsedSeconds)
      : undefined;
  const selectedOrderHasDeliveryTimeline =
    !!selectedOrder && hasDeliveryStatusHistory(selectedOrder);
  const selectedOrderDeliveryTimeline =
    selectedOrder && selectedOrderHasDeliveryTimeline
      ? buildDeliveryStatusTimeline(selectedOrder)
      : null;
  const selectedOrderIsCancelled =
    selectedOrder?.orderStatus === "cancelled";
  const handleLogout = () => {
    Cookies.remove("emptoken");
    Cookies.remove("employeeUsername");
    window.location.href = "/kitchen";
  };
  const showSidebarLabels = isSidebarExpanded;
  const sidebarButtonBaseClass =
    `w-full rounded-xl py-2.5 flex items-center font-outfit text-sm transition-colors ${
      showSidebarLabels ? "px-3 gap-3 justify-start" : "px-0 justify-center"
    }`;
  const handleOrdersScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 80;

    if (!nearBottom) return;
    setVisibleOrderCount((prev) => {
      if (prev >= renderedOrders.length) return prev;
      return Math.min(prev + ORDERS_BATCH_SIZE, renderedOrders.length);
    });
  };
  const handleSidebarToggle = () => {
    setIsSidebarExpanded((prev) => !prev);
  };

  const sidebarMenuItems = [
    {
      key: "printer",
      label: "Printer Connection",
      icon: Printer,
      onClick: () => setShowKitchenPrinterModal(true),
      isActive: showKitchenPrinterModal,
    },
    {
      key: "recent",
      label: "Recent Orders",
      icon: ClipboardList,
      onClick: () => setActiveView("recent"),
      isActive: activeView === "recent",
      badge: unseenRecentOrderCount,
    },
    {
      key: "scheduled",
      label: "Scheduled Orders",
      icon: CalendarClock,
      onClick: () => setActiveView("scheduled"),
      isActive: activeView === "scheduled",
      badge: unseenScheduledOrderCount,
    },
    {
      key: "dinein",
      label: "Dine-in Orders",
      icon: UtensilsCrossed,
      onClick: () => setActiveView("dinein"),
      isActive: activeView === "dinein",
      badge: unseenDineInOrderCount,
    },
    {
      key: "orderhistory",
      label: "Order History",
      icon: History,
      onClick: () => setActiveView("history"),
      isActive: activeView === "history",
    },
    {
      key: "settings",
      label: "Settings",
      icon: Settings,
      onClick: () => setShowSettingsPanel(true),
      isActive: false,
    },
    {
      key: "refresh",
      label: "Refresh",
      icon: RefreshCcw,
      onClick: () => fetchOrders(1),
      isActive: false,
    },
  ];

  return (
    <>
      <Buzz ref={buzzRef} />

      {showKitchenPrinterModal && (
        <KitchenPrinterModal
          onClose={() => {
            setShowKitchenPrinterModal(false);
            // Only retry queued prints if user actually paired a printer
            if (getKitchenPrinterName()) {
              void processPrintQueue();
            }
          }}
        />
      )}

      <div className="h-dvh w-full bg-[#EDF3F6] flex overflow-hidden">
        <aside
          className={`h-full shrink-0 bg-black text-white border-r border-slate-700/50 flex flex-col transition-all duration-300 ${
            isSidebarExpanded ? "w-44 lg:w-56" : "w-12 sm:w-14 md:w-16"
          }`}
        >
          <div className="px-3 py-4 border-b border-slate-700/60">
            <button
              type="button"
              onClick={handleSidebarToggle}
              className={`${sidebarButtonBaseClass} text-slate-100 hover:bg-slate-700/60`}
            >
              <Menu className="w-5 h-5 shrink-0" />
              {showSidebarLabels && <span>Kitchen</span>}
            </button>
          </div>

          <div className="px-2 sm:px-3 py-4 space-y-2 overflow-y-auto">
            {sidebarMenuItems.map((item) => {
              const Icon = item.icon;
              const badgeValue = item.badge ?? 0;
              const hasBadge = badgeValue > 0;
              const badgeLabel = badgeValue > 99 ? "99+" : badgeValue;
              return (
                <button
                  key={item.key}
                  type="button"
                  title={item.label}
                  onClick={item.onClick}
                  className={`${sidebarButtonBaseClass} ${
                    item.isActive
                      ? "bg-gray-200 text-black"
                      : "text-slate-200 hover:bg-slate-700/60"
                  }`}
                >
                  <span className="relative flex items-center justify-center">
                    <Icon className="w-5 h-5 shrink-0" />
                    {!showSidebarLabels && hasBadge && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] rounded-full bg-red-500 text-white flex items-center justify-center border border-slate-900">
                        {badgeLabel}
                      </span>
                    )}
                  </span>
                  {showSidebarLabels && (
                    <span className="flex items-center gap-2">
                      {item.label}
                      {hasBadge && (
                        <span className="min-w-[20px] px-1.5 h-5 text-xs rounded-full bg-red-500 text-white flex items-center justify-center">
                          {badgeLabel}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-auto px-3 py-4 border-t border-slate-700/60">
            <button
              type="button"
              title="Logout"
              onClick={handleLogout}
              className={`${sidebarButtonBaseClass} text-white`}
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {showSidebarLabels && <span>Logout</span>}
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 flex flex-col">
          <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="font-outfit text-xl sm:text-2xl text-slate-900">
                  {activeViewLabel}
                </h1>
              </div>

              <div className="relative shrink-0 self-start sm:self-auto" ref={sortMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsSortMenuOpen((prev) => !prev)}
                  className="h-10 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-outfit text-sm flex items-center gap-2"
                  title="Sort by time"
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span>Sort</span>
                </button>

                {isSortMenuOpen && (
                  <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-md z-50">
                    <button
                      type="button"
                      onClick={() => {
                        setSortOrder("older");
                        setIsSortMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-outfit ${
                        sortOrder === "older" ? "bg-[#ECF0F2]" : ""
                      }`}
                    >
                      Older First
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSortOrder("newest");
                        setIsSortMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-outfit ${
                        sortOrder === "newest" ? "bg-[#ECF0F2]" : ""
                      }`}
                    >
                      Newest First
                    </button>
                  </div>
                )}
              </div>
            </div>

            {activeView === "history" && (
              <div className="mt-3 relative w-full max-w-md">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={15}
                />
                <input
                  type="text"
                  value={historySearchTerm}
                  onChange={(event) => setHistorySearchTerm(event.target.value)}
                  placeholder="Search by phone number or order number"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm font-outfit text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </div>
            )}
          </header>

          <div
            ref={ordersScrollRef}
            onScroll={handleOrdersScroll}
            className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 lg:px-6 py-3 lg:py-4"
          >
            {currentOrders.length > 0 ? (
              <div className="overflow-x-auto md:overflow-x-visible">
                <div className="min-w-[600px] sm:min-w-[660px] md:min-w-0">
                  <div
                    className={`grid ${ORDERS_ROW_COLUMNS_CLASS} min-w-[600px] sm:min-w-[660px] md:min-w-0 px-3 lg:px-4 pb-2 text-xs uppercase tracking-wide text-slate-500 font-outfit`}
                  >
                    <span>Customer</span>
                    <span>Amount</span>
                    <span>Status</span>
                    <span className="text-right">
                      {activeView === "history" ? "Details" : "Actions"}
                    </span>
                    <span />
                  </div>
                  <div className="space-y-3 pb-3">{currentOrders}</div>
                </div>
              </div>
            ) : (
              <div className="h-full min-h-[240px] rounded-2xl border border-dashed border-slate-300 bg-white/70 flex items-center justify-center font-outfit text-slate-500">
                No orders found for {activeViewLabel.toLowerCase()}.
              </div>
            )}
          </div>
        </main>
      </div>

      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
          onClick={closeOrderDetail}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-200">
              <div>
                <div className="text-lg font-semibold font-outfit text-slate-900">
                  {getOrderNumberLabel(selectedOrder)}
                </div>
                <div className="text-sm font-outfit text-slate-500">
                  {formatChoiceLabel(selectedOrder.choice)}
                </div>
              </div>
              <button
                type="button"
                onClick={closeOrderDetail}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-outfit text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 overflow-y-auto max-h-[calc(90vh-72px)]">
              <div className="flex flex-wrap items-center gap-4 text-sm font-outfit text-slate-600">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                    selectedOrder.orderStatus === "ready"
                      ? "bg-emerald-100 text-emerald-700"
                      : selectedOrder.orderStatus === "pickedup"
                        ? "bg-teal-100 text-teal-700"
                      : selectedOrder.orderStatus === "preparing"
                        ? "bg-amber-100 text-amber-700"
                        : selectedOrder.orderStatus === "scheduled"
                          ? "bg-slate-100 text-slate-700"
                          : selectedOrder.orderStatus === "cancelled"
                            ? "bg-red-100 text-red-700"
                            : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {selectedOrder.orderStatus === "ready"
                    ? "Completed"
                    : selectedOrder.orderStatus === "pickedup"
                      ? "Picked Up"
                    : selectedOrder.orderStatus === "preparing"
                      ? "Preparing"
                      : selectedOrder.orderStatus === "scheduled"
                        ? "Scheduled"
                        : selectedOrder.orderStatus === "cancelled"
                          ? "Cancelled"
                          : "Received"}
                </span>
                <span>
                  {selectedOrder.orderStatus === "scheduled"
                    ? formatScheduledDateTime(
                        selectedOrder.scheduledAt,
                        selectedOrderTimezone,
                      )
                    : `Ordered at ${formatOrderTime(
                        selectedOrder.createdAt,
                        selectedOrderTimezone,
                      )}`}
                  {selectedOrder.orderStatus === "ready" &&
                    selectedOrderPickupDeadline && (
                      <>
                        {" • Pickup: "}
                        <CountdownTo
                          targetTs={selectedOrderPickupDeadline}
                          className="text-slate-600"
                          lateClassName="text-red-600 font-semibold"
                        />
                      </>
                    )}
                  {selectedOrder.orderStatus === "ready" &&
                    selectedOrderPrepTimeSeconds != null && (
                      <>
                        {" • Prepared in: "}
                        <span className="text-slate-600">
                          {formatTime(selectedOrderPrepTimeSeconds)}
                        </span>
                      </>
                    )}
                  {selectedOrder.orderStatus === "preparing" &&
                    selectedOrderPrepCountdown && (
                      <>
                        {selectedOrderPrepLate ? (
                          <span className="text-red-600 font-semibold">
                            {" • Order Late"}
                          </span>
                        ) : (
                          <span className="text-slate-600">
                            {" • Preparing: "}
                            {selectedOrderPrepCountdown}
                          </span>
                        )}
                      </>
                    )}
                </span>
                {selectedOrderPhoneRaw ? (
                  <a
                    href={selectedOrderPhoneHref}
                    className="flex items-center gap-1 text-slate-700 hover:text-slate-900"
                  >
                    <img
                      src={call}
                      alt="Phone"
                      className="w-4 h-4 rounded-full bg-white"
                    />
                    {selectedOrderPhoneRaw}
                  </a>
                ) : (
                  <span className="flex items-center gap-1 text-slate-500">
                    <img
                      src={call}
                      alt="Phone"
                      className="w-4 h-4 rounded-full bg-white"
                    />
                    N/A
                  </span>
                )}
              </div>

              {activeView !== "history" && (
                <div className="flex flex-wrap items-center gap-2">
                  {renderOrderActionButtons(selectedOrder, "modal")}
                </div>
              )}

              {selectedOrderTimeline && (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 text-sm font-semibold font-outfit text-slate-700">
                    Order Status
                  </div>
                  <div className="px-4 py-4">
                    <div className="relative">
                      {selectedOrderTimeline.steps.length > 1 && (
                        <div
                          className={`absolute top-1.5 h-[2px] ${
                            selectedOrderIsCancelled
                              ? "bg-red-400"
                              : "bg-emerald-400"
                          }`}
                          style={getTimelineProgressStyle(
                            selectedOrderTimeline.activeIndex,
                            selectedOrderTimeline.steps.length,
                          )}
                        />
                      )}
                      <div
                        className="grid gap-2"
                        style={{
                          gridTemplateColumns: `repeat(${selectedOrderTimeline.steps.length}, minmax(0, 1fr))`,
                        }}
                      >
                        {selectedOrderTimeline.steps.map((step, idx) => {
                          const isActive =
                            idx === selectedOrderTimeline.activeIndex;
                          const isComplete =
                            idx < selectedOrderTimeline.activeIndex;
                          const dotClass = selectedOrderIsCancelled
                            ? "bg-red-500"
                            : "bg-emerald-500";
                          return (
                            <div
                              key={step.key}
                              className="flex flex-col items-center text-center"
                            >
                              <span
                                className={`w-3 h-3 rounded-full ${
                                  isActive || isComplete ? dotClass : "bg-slate-300"
                                } ${
                                  isActive
                                    ? selectedOrderIsCancelled
                                      ? "ring-4 ring-red-100"
                                      : "ring-4 ring-emerald-100"
                                    : ""
                                }`}
                              />
                              <span className="mt-2 text-xs font-semibold font-outfit text-slate-700">
                                {step.label}
                              </span>
                              <span className="mt-0.5 text-[11px] font-outfit text-slate-500 leading-tight">
                                {step.timeLabel}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedOrderHasDeliveryTimeline &&
                selectedOrderDeliveryTimeline && (
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 text-sm font-semibold font-outfit text-slate-700">
                      Delivery Status
                    </div>
                    <div className="px-4 py-4">
                      <div className="relative">
                        {selectedOrderDeliveryTimeline.steps.length > 1 && (
                          <div
                            className="absolute top-1.5 h-[2px] bg-sky-400"
                            style={getTimelineProgressStyle(
                              selectedOrderDeliveryTimeline.activeIndex,
                              selectedOrderDeliveryTimeline.steps.length,
                            )}
                          />
                        )}
                        <div
                          className="grid gap-2"
                          style={{
                            gridTemplateColumns: `repeat(${selectedOrderDeliveryTimeline.steps.length}, minmax(0, 1fr))`,
                          }}
                        >
                          {selectedOrderDeliveryTimeline.steps.map(
                            (step, idx) => {
                              const isActive =
                                idx === selectedOrderDeliveryTimeline.activeIndex;
                              const isComplete =
                                idx < selectedOrderDeliveryTimeline.activeIndex;
                              return (
                                <div
                                  key={step.key}
                                  className="flex flex-col items-center text-center"
                                >
                                  <span
                                    className={`w-3 h-3 rounded-full ${
                                      isActive || isComplete
                                        ? "bg-sky-500"
                                        : "bg-slate-300"
                                    } ${isActive ? "ring-4 ring-sky-100" : ""}`}
                                  />
                                  <span className="mt-2 text-xs font-semibold font-outfit text-slate-700">
                                    {step.label}
                                  </span>
                                  <span className="mt-0.5 text-[11px] font-outfit text-slate-500 leading-tight">
                                    {step.timeLabel}
                                  </span>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 text-sm font-semibold font-outfit text-slate-700">
                  Items
                </div>
                <div className="divide-y divide-slate-200">
                  {selectedOrder.items.map((item, idx) => {
                    const key = getItemKey(selectedOrder._id, item.itemId, idx);
                    const isDeleted =
                      deletedItems[key] ||
                      item.itemstatus === "cancelled" ||
                      selectedOrder.orderStatus === "cancelled";
                    return (
                      <div
                        key={key}
                        className={`px-4 py-3 text-sm font-outfit ${
                          isDeleted ? "line-through text-red-500" : "text-slate-900"
                        }`}
                      >
                        <div className="font-semibold">
                          {item.quantity}x {item.kitchenName || item.name}
                        </div>
                        <ItemCustomizations item={item} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSettingsPanel && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
          onClick={() => setShowSettingsPanel(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
              <div className="text-lg font-semibold font-outfit text-slate-900">
                Settings
              </div>
              <button
                type="button"
                onClick={() => setShowSettingsPanel(false)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-outfit text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
            <div className="max-h-[calc(90vh-72px)] overflow-y-auto">
              <Setting embedded />
            </div>
          </div>
        </div>
      )}

      {scheduleModalOrder && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-4">
            <div className="text-black font-outfit font-semibold text-lg">
              Delay Scheduled Order
            </div>
            <p className="text-sm text-gray-600 mt-1 font-outfit">
              Order {getOrderNumberLabel(scheduleModalOrder)}
            </p>

            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 text-sm font-outfit disabled:opacity-40"
                onClick={() => setDelayMinutes((prev) => Math.max(0, prev - 5))}
                disabled={delayMinutes <= 0 || isUpdatingSchedule}
              >
                -5 mins
              </button>

              <div className="text-base font-semibold text-black min-w-[100px] text-center">
                +{delayMinutes} mins
              </div>

              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 text-sm font-outfit disabled:opacity-40"
                onClick={() =>
                  setDelayMinutes((prev) => Math.min(60, prev + 5))
                }
                disabled={delayMinutes >= 60 || isUpdatingSchedule}
              >
                +5 mins
              </button>
            </div>

            <div className="mt-4 text-sm font-outfit text-gray-700">
              New time:{" "}
              <span className="font-semibold text-black">
                {formatScheduledDateTime(
                  delayedSchedulePreview ?? undefined,
                  delayedScheduleTimezone,
                )}
              </span>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 text-sm font-outfit"
                onClick={closeDelayScheduleModal}
                disabled={isUpdatingSchedule}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded-md bg-black text-white text-sm font-outfit disabled:opacity-50"
                onClick={applyScheduledDelay}
                disabled={delayMinutes <= 0 || isUpdatingSchedule}
              >
                {isUpdatingSchedule ? "Updating..." : "Apply Delay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== PASSCODE MODAL ===== */}
      {passcodePendingAction && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-5">
            <div className="text-black font-outfit font-semibold text-lg mb-1">
              Enter Passcode to delete{" "}
              {passcodePendingAction.type === "deleteItem" ? "Item" : "Order"}
            </div>
            <p className="text-sm text-gray-500 font-outfit mb-4">
              {passcodePendingAction.type === "deleteItem" ? (
                <>
                  <span>
                    Order Number: {passcodePendingAction.orderNumber ?? "N/A"}
                  </span>
                  <br />
                  <span>Item: {passcodePendingAction.itemName}</span>
                </>
              ) : (
                <span>
                  Order Number: {passcodePendingAction.orderNumber ?? "N/A"}
                </span>
              )}
            </p>
            <div
              className="flex justify-center gap-2 mb-4 cursor-text"
              onClick={() => passcodeInputRef.current?.focus()}
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-10 h-12 border-2 rounded-lg flex items-center justify-center text-xl font-mono font-bold transition-colors ${
                    passcodeInput[i]
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-gray-50 text-gray-400"
                  }`}
                >
                  {passcodeInput[i] ? "●" : ""}
                </div>
              ))}
            </div>
            {/* Invisible input to capture typed digits */}
            <input
              ref={passcodeInputRef}
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              autoFocus
              value={passcodeInput}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setPasscodeInput(val);
                setPasscodeError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && passcodeInput.length === 6)
                  handlePasscodeSubmit();
              }}
              className="absolute opacity-0 w-0 h-0 pointer-events-none"
              readOnly={false}
              aria-hidden="true"
            />
            {passcodeError && (
              <p className="text-red-500 text-sm text-center mb-2">
                {passcodeError}
              </p>
            )}
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  setPasscodePendingAction(null);
                  setPasscodeInput("");
                  setPasscodeError("");
                }}
                disabled={isVerifyingPasscode}
                className="flex-1 py-2 border border-gray-300 rounded-md text-sm font-outfit disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePasscodeSubmit}
                disabled={isVerifyingPasscode || passcodeInput.length !== 6}
                className="flex-1 py-2 bg-red-600 text-white rounded-md text-sm font-outfit disabled:opacity-50"
              >
                {isVerifyingPasscode ? "Verifying..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingFullCancelOrder && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-4">
            <div className="text-black font-outfit font-semibold text-lg">
              Confirm Order Cancellation
            </div>
            <p className="text-sm text-gray-700 mt-2 font-outfit">
              Are you sure you want to cancel{" "}
              <span className="font-semibold">
                {getOrderNumberLabel({
                  _id: pendingFullCancelOrder.orderId,
                  orderNumber: pendingFullCancelOrder.orderNumber,
                })}
              </span>
              ? This will cancel the full order.
            </p>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 text-sm font-outfit disabled:opacity-50"
                onClick={closeCancelOrderModal}
                disabled={isCancellingOrder}
              >
                No
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded-md bg-red-600 text-white text-sm font-outfit disabled:opacity-50"
                onClick={handleCancelFullOrder}
                disabled={isCancellingOrder}
              >
                {isCancellingOrder ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KitchenOrders;
